/**
 * Platform API Service
 * Centralized API client for the Marketing & Sales Platform
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

// Platform API base — product-aware:
//   - Triveny (app.triveny.ai)  → ms-api-hots.onrender.com (issues + accepts platform JWT)
//   - WeaveIn (weavein.ai)      → weavein-api.onrender.com (legacy unified backend)
// Use config.weaveinApi.baseUrl (which is already product-routed in src/config.ts)
// instead of resolving env vars here — otherwise a Triveny platform JWT hits
// weavein-api which doesn't share the PLATFORM_JWT_SECRET and 401s the call.
import { config as productConfig } from '@/src/config';
const PLATFORM_BACKEND_URL = (
  productConfig.weaveinApi.baseUrl ||
  import.meta.env.VITE_PLATFORM_API_URL ||
  productConfig.weaveinApi.baseUrl ||
  'https://weavein-api.onrender.com'
).trim();
const API_BASE_URL = `${PLATFORM_BACKEND_URL}/api/platform/v1`;

// ============================================================================
// TYPES
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta?: {
    page?: number;
    pageSize?: number;
    totalCount?: number;
    totalPages?: number;
  };
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// API CLIENT
// ============================================================================

class PlatformApiClient {
  private baseUrl: string;
  private tenantId: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;

    // Try to get tenant from localStorage
    if (typeof window !== 'undefined') {
      this.tenantId = localStorage.getItem('platform_tenant_slug');
    }
  }

  setTenant(tenantId: string) {
    this.tenantId = tenantId;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add active CBO ID for org-level data isolation
    const cboId = localStorage.getItem('active_cbo_id');
    if (cboId) {
      headers['X-CBO-ID'] = cboId;
    }

    return headers;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: unknown
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: this.getHeaders(),
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: json.error?.code || 'REQUEST_FAILED',
            message: json.error?.message || 'Request failed',
            details: json.error?.details,
          },
        };
      }

      // Handle both wrapped { data: ... } and direct response formats
      return {
        success: true,
        data: json.data !== undefined ? json.data : json,
        meta: json.meta,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error occurred',
        },
      };
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url = `${endpoint}?${searchParams.toString()}`;
    }
    return this.request<T>('GET', url);
  }

  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data);
  }

  async put<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data);
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint);
  }

  /**
   * Fetch a binary resource (e.g. a ticket attachment image) using the same
   * auth headers as request() and turn it into an object URL the browser can
   * render in an <img src>. Returns null on any failure.
   *
   * A bare <img src> would NOT send Authorization / X-CBO-ID, so image
   * attachments must be fetched authenticated and blobbed here.
   * Callers must URL.revokeObjectURL() the returned URL when done.
   */
  async fetchObjectUrl(endpoint: string): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('fetchObjectUrl failed:', error);
      return null;
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let apiClient: PlatformApiClient | null = null;

export function getPlatformApi(): PlatformApiClient {
  if (!apiClient) {
    apiClient = new PlatformApiClient();
  }
  return apiClient;
}

// ============================================================================
// WEB PRESENCE API
// ============================================================================

export interface Page {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  content?: string;
  metaTitle?: string;
  metaDescription?: string;
  views: number;
  createdAt: string;
  updatedAt: string;
}

export interface Form {
  id: string;
  name: string;
  type: 'contact' | 'lead' | 'survey' | 'registration';
  fields: FormField[];
  submissions: number;
  conversionRate: number;
  createdAt: string;
}

export interface FormField {
  id: string;
  name: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
  required: boolean;
  options?: string[];
}

export interface Asset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document';
  size: number;
  url: string;
  mimeType: string;
  uploadedAt: string;
}

export const webPresenceApi = {
  // Pages
  getPages: (params?: PaginationParams) =>
    getPlatformApi().get<Page[]>('/web-presence/pages', params as Record<string, string | number>),

  getPage: (id: string) =>
    getPlatformApi().get<Page>(`/web-presence/pages/${id}`),

  createPage: (data: Partial<Page>) =>
    getPlatformApi().post<Page>('/web-presence/pages', data),

  updatePage: (id: string, data: Partial<Page>) =>
    getPlatformApi().patch<Page>(`/web-presence/pages/${id}`, data),

  deletePage: (id: string) =>
    getPlatformApi().delete(`/web-presence/pages/${id}`),

  // Landing Pages
  getLandingPages: (params?: PaginationParams) =>
    getPlatformApi().get<Page[]>('/web-presence/landing-pages', params as Record<string, string | number>),

  createLandingPage: (data: Partial<Page>) =>
    getPlatformApi().post<Page>('/web-presence/landing-pages', data),

  // Forms
  getForms: (params?: PaginationParams) =>
    getPlatformApi().get<Form[]>('/web-presence/forms', params as Record<string, string | number>),

  createForm: (data: Partial<Form> | Record<string, any>) =>
    getPlatformApi().post<Form>('/web-presence/forms', data),

  updateForm: (id: string, data: Record<string, any>) =>
    getPlatformApi().patch<Form>(`/web-presence/forms/${id}`, data),

  // Assets
  getAssets: (params?: PaginationParams) =>
    getPlatformApi().get<Asset[]>('/web-presence/assets', params as Record<string, string | number>),

  deleteAsset: (id: string) =>
    getPlatformApi().delete(`/web-presence/assets/${id}`),

  // Content Types
  getContentTypes: () =>
    getPlatformApi().get<any[]>('/web-presence/content-types'),

  createContentType: (data: any) =>
    getPlatformApi().post<any>('/web-presence/content-types', data),

  updateContentType: (id: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/content-types/${id}`, data),

  deleteContentType: (id: string) =>
    getPlatformApi().delete(`/web-presence/content-types/${id}`),

  getContentItems: (typeId: string) =>
    getPlatformApi().get<any[]>(`/web-presence/content-types/${typeId}/items`),

  createContentItem: (typeId: string, data: any) =>
    getPlatformApi().post<any>(`/web-presence/content-types/${typeId}/items`, data),

  updateContentItem: (id: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/content-items/${id}`, data),

  deleteContentItem: (id: string) =>
    getPlatformApi().delete(`/web-presence/content-items/${id}`),

  // Design Tokens
  getDesignTokens: (siteId: string) =>
    getPlatformApi().get<any[]>(`/web-presence/sites/${siteId}/design-tokens`),

  // NOTE: the create route is nested under the site (POST /sites/{id}/design-tokens);
  // callers pass site_id in `data`. Posting to a flat /design-tokens 404s.
  createDesignToken: (data: any) =>
    getPlatformApi().post<any>(`/web-presence/sites/${data.site_id}/design-tokens`, data),

  updateDesignToken: (id: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/design-tokens/${id}`, data),

  deleteDesignToken: (id: string) =>
    getPlatformApi().delete(`/web-presence/design-tokens/${id}`),

  // Components
  getComponents: () =>
    getPlatformApi().get<any[]>('/web-presence/components'),

  // Section Templates
  getSectionTemplates: () =>
    getPlatformApi().get<any[]>('/web-presence/section-templates'),

  // Code Snippets
  getCodeSnippets: () =>
    getPlatformApi().get<any[]>('/web-presence/code-snippets'),

  createCodeSnippet: (data: any) =>
    getPlatformApi().post<any>('/web-presence/code-snippets', data),

  deleteCodeSnippet: (id: string) =>
    getPlatformApi().delete(`/web-presence/code-snippets/${id}`),

  // Permissions
  getPermissions: () =>
    getPlatformApi().get<any[]>('/web-presence/permissions'),

  savePermission: (data: any) =>
    getPlatformApi().post<any>('/web-presence/permissions', data),

  updatePermission: (userId: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/permissions/${userId}`, data),

  // Team Members (org-integrated)
  getTeamMembers: () =>
    getPlatformApi().get<any[]>('/web-presence/team-members'),

  // Role Mappings (org role → WP role defaults)
  getRoleMappings: () =>
    getPlatformApi().get<any[]>('/web-presence/role-mappings'),

  updateRoleMapping: (systemRole: string, data: any) =>
    getPlatformApi().put<any>(`/web-presence/role-mappings/${systemRole}`, data),

  // Activity Log
  getActivityLog: (params?: Record<string, string | number>) =>
    getPlatformApi().get<any[]>('/web-presence/activity-log', params),

  // Redirects
  getRedirects: (siteId: string) =>
    getPlatformApi().get<any[]>(`/web-presence/sites/${siteId}/redirects`),

  createRedirect: (data: any) =>
    getPlatformApi().post<any>('/web-presence/redirects', data),

  updateRedirect: (id: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/redirects/${id}`, data),

  deleteRedirect: (id: string) =>
    getPlatformApi().delete(`/web-presence/redirects/${id}`),

  // Sites
  getSites: () =>
    getPlatformApi().get<any[]>('/web-presence/sites'),

  createSite: (data: { site_name: string; site_type?: string; domain?: string }) =>
    getPlatformApi().post<any>('/web-presence/sites', data),

  // Analytics / Dashboard
  getDashboardMetrics: (days?: number) =>
    getPlatformApi().get<any>('/web-presence/analytics/dashboard', days ? { days } : undefined),

  // ── AI Design Assistant ────────────────────────────────────────────────
  aiGenerate: (data: {
    content_type: string;
    prompt: string;
    context?: Record<string, unknown>;
    model_preference?: 'fast' | 'quality';
    system_prompt_override?: string;
    site_id?: string;
    page_id?: string;
  }) => getPlatformApi().post<any>('/web-presence/ai/generate', data),

  aiGenerateTokens: (data: {
    image_url?: string;
    reference_text?: string;
    seed_color?: string;
    site_id?: string;
  }) => getPlatformApi().post<any>('/web-presence/ai/generate-tokens', data),

  aiEditBlock: (data: {
    block: Record<string, unknown>;
    instruction: string;
    site_id?: string;
    page_id?: string;
  }) => getPlatformApi().post<any>('/web-presence/ai/edit-block', data),

  // Analyze a public reference website → suggested brand tokens (review + apply).
  aiAnalyzeSite: (data: { url: string; seed_color?: string; site_id?: string }) =>
    getPlatformApi().post<any>('/web-presence/ai/analyze-site', data),

  // Analyze uploaded reference documents (logo / PDFs / docs) → suggested tokens.
  aiAnalyzeDocuments: (data: {
    doc_urls: string[];
    image_url?: string;
    reference_text?: string;
    seed_color?: string;
    site_id?: string;
  }) => getPlatformApi().post<any>('/web-presence/ai/analyze-documents', data),

  // Stock photo search (Pexels via a server-side proxy). Same shape as stockVideos.
  stockImages: (q = '', perPage = 16) =>
    getPlatformApi().get<{ enabled: boolean; items: Array<{ id: number; thumbnail: string; url: string; width?: number; height?: number }>; error?: string }>(
      `/web-presence/stock/images?q=${encodeURIComponent(q)}&per_page=${perPage}`,
    ),

  // Stock video search (Pexels via a server-side proxy so the key stays hidden).
  // Returns { enabled, items }; enabled=false when no PEXELS_API_KEY is configured.
  stockVideos: (q = '', perPage = 12) =>
    getPlatformApi().get<{ enabled: boolean; items: Array<{ id: number; thumbnail: string; url: string; width?: number; duration?: number }>; error?: string }>(
      `/web-presence/stock/videos?q=${encodeURIComponent(q)}&per_page=${perPage}`,
    ),

  // Web Presence v2 (Craft.js) document persistence — tenant-scoped via JWT.
  craftGet: (docKey = 'home') =>
    getPlatformApi().get<{ doc_key: string; document: any; meta?: any; updated_at?: string }>(
      `/web-presence/craft?doc_key=${encodeURIComponent(docKey)}`,
    ),
  // `meta` (per-page SEO + code snippets) is optional; when omitted the backend
  // preserves stored meta, so a document-only save never wipes SEO/scripts.
  craftSave: (document: any, docKey = 'home', meta?: any) =>
    getPlatformApi().put<any>(
      '/web-presence/craft',
      meta !== undefined ? { doc_key: docKey, document, meta } : { doc_key: docKey, document },
    ),
  // Upload an image/logo/document to the tenant asset store (S3-backed
  // wp_assets) so it ALSO appears in the Assets & Media tab + the builder's
  // asset picker. Returns { url, id, name }. Falls back to the standalone
  // design-upload endpoint if the unified one is unavailable.
  uploadAsset: async (file: File): Promise<{ url?: string; id?: string; name?: string } | null> => {
    const token = localStorage.getItem('auth_token') || '';
    const post = async (path: string) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) return null;
      return await res.json();
    };
    try {
      const a = await post('/web-presence/assets');
      if (a) {
        // Ignore the legacy placeholder URL ("/storage/<slug>/…") from older
        // backends that haven't deployed the real S3 upload yet — fall through.
        const url = a.cdn_url || a.storage_url || a.url;
        if (url && !String(url).startsWith('/storage/')) {
          return { url, id: a.id, name: a.file_name || a.name || file.name };
        }
      }
    } catch {
      /* fall through to standalone uploader */
    }
    try {
      const b = await post('/web-presence/assets/upload');
      if (b && (b.url || b.cdn_url || b.storage_url)) {
        return { url: b.url || b.cdn_url || b.storage_url, id: b.id, name: b.name || file.name };
      }
    } catch {
      /* give up — caller handles null (data-URL fallback) */
    }
    return null;
  },
  craftPublish: (docKey = 'home') =>
    getPlatformApi().post<any>('/web-presence/craft/publish', { doc_key: docKey }),
  craftUnpublish: (docKey = 'home') =>
    getPlatformApi().post<any>('/web-presence/craft/unpublish', { doc_key: docKey }),

  aiRefine: (data: {
    generation_id: string;
    feedback: string;
    context?: Record<string, unknown>;
  }) => getPlatformApi().post<any>('/web-presence/ai/refine', data),

  aiGenerateSeo: (data: {
    page_content: Record<string, unknown>;
    target_keywords?: string[];
  }) => getPlatformApi().post<any>('/web-presence/ai/generate-seo', data),

  aiApply: (data: {
    generation_id: string;
    page_id: string;
    merge_mode?: 'replace' | 'append';
  }) => getPlatformApi().post<any>('/web-presence/ai/apply', data),

  aiGetGenerations: (params?: {
    content_type?: string;
    page_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => getPlatformApi().get<any[]>('/web-presence/ai/generations', params as Record<string, string | number>),

  aiGetGeneration: (id: string) =>
    getPlatformApi().get<any>(`/web-presence/ai/generations/${id}`),

  aiGetTemplates: (params?: { content_type?: string; category?: string }) =>
    getPlatformApi().get<any>('/web-presence/ai/templates', params as Record<string, string | number>),

  aiCreateTemplate: (data: {
    template_name: string;
    content_type: string;
    description?: string;
    system_prompt: string;
    category?: string;
    tags?: string[];
  }) => getPlatformApi().post<any>('/web-presence/ai/templates', data),

  aiGetTemplate: (id: string) =>
    getPlatformApi().get<any>(`/web-presence/ai/templates/${id}`),

  // ── Content Outputs (managed lifecycle for all generated content) ───
  getContentOutputs: (params?: { content_type?: string; status?: string; limit?: number }) =>
    getPlatformApi().get<any[]>('/web-presence/content-outputs', params as Record<string, string | number>),

  createContentOutput: (data: {
    generation_id: string;
    title: string;
    content_type: string;
    content: any;
  }) => getPlatformApi().post<any>('/web-presence/content-outputs', data),

  updateContentOutput: (id: string, data: any) =>
    getPlatformApi().patch<any>(`/web-presence/content-outputs/${id}`, data),

  deleteContentOutput: (id: string) =>
    getPlatformApi().delete(`/web-presence/content-outputs/${id}`),

  publishContentOutput: (id: string, data: { channel: string; metadata?: Record<string, unknown> }) =>
    getPlatformApi().post<any>(`/web-presence/content-outputs/${id}/publish`, data),
};

// ============================================================================
// MARKETING API
// ============================================================================

export type CampaignChannel = 'email' | 'sms' | 'whatsapp' | 'voice' | 'social';
export type CampaignType = 'email' | 'sms' | 'whatsapp' | 'voice' | 'social' | 'multi-channel';

export interface Campaign {
  id: string;
  name: string;
  type: CampaignType;
  channels?: CampaignChannel[];  // For multi-channel campaigns
  status: 'draft' | 'scheduled' | 'running' | 'completed' | 'paused';
  subject?: string;
  content?: string;
  sentCount: number;
  openRate: number;
  clickRate: number;
  scheduledAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Automation {
  id: string;
  name: string;
  trigger: string;
  status: 'active' | 'paused' | 'draft';
  steps: AutomationStep[];
  enrolled: number;
  completed: number;
  createdAt: string;
}

export interface AutomationStep {
  id: string;
  type: 'email' | 'wait' | 'condition' | 'action';
  config: Record<string, unknown>;
  order: number;
}

export interface Contact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  score: number;
  status: 'active' | 'unsubscribed' | 'bounced';
  tags: string[];
  customFields: Record<string, unknown>;
  lastActivityAt: string;
  createdAt: string;
}

export interface ContactList {
  id: string;
  name: string;
  type: 'static' | 'dynamic';
  count: number;
  criteria?: Record<string, unknown>;
  createdAt: string;
}

export const marketingApi = {
  // Campaigns
  getCampaigns: (params?: PaginationParams) =>
    getPlatformApi().get<Campaign[]>('/marketing/campaigns', params as Record<string, string | number>),

  getCampaign: (id: string) =>
    getPlatformApi().get<Campaign>(`/marketing/campaigns/${id}`),

  createCampaign: (data: Partial<Campaign>) =>
    getPlatformApi().post<Campaign>('/marketing/campaigns', data),

  updateCampaign: (id: string, data: Partial<Campaign>) =>
    getPlatformApi().put<Campaign>(`/marketing/campaigns/${id}`, data),

  sendCampaign: (id: string) =>
    getPlatformApi().post(`/marketing/campaigns/${id}/launch`, {}),

  pauseCampaign: (id: string) =>
    getPlatformApi().post(`/marketing/campaigns/${id}/pause`, {}),

  // Campaign drip steps (ordered channel messages with delays).
  getCampaignSteps: (id: string) =>
    getPlatformApi().get<any[]>(`/marketing/campaigns/${id}/steps`),
  // Per-campaign multi-channel performance (drip sent/failed/pending + replies +
  // email opens/clicks) for the Analytics tab.
  getCampaignsSummary: () =>
    getPlatformApi().get<{ totals: any; campaigns: any[] }>(`/marketing/campaigns-summary`),
  saveCampaignSteps: (id: string, steps: Array<{ channel: string; subject?: string; content?: string; delay_hours: number }>) =>
    getPlatformApi().put<any[]>(`/marketing/campaigns/${id}/steps`, { steps }),

  // Marketing automations — persisted rules (event trigger → action). The live
  // spine engine.
  getAutomations: () => getPlatformApi().get<any[]>(`/marketing/automations`),
  createAutomation: (data: {
    name: string;
    trigger_type: string;
    trigger_config?: Record<string, unknown>;
    action_type: string;
    action_config?: Record<string, unknown>;
    status?: string;
  }) => getPlatformApi().post<any>(`/marketing/automations`, data),
  updateAutomation: (id: string, data: Record<string, unknown>) =>
    getPlatformApi().patch<any>(`/marketing/automations/${id}`, data),
  deleteAutomation: (id: string) => getPlatformApi().delete(`/marketing/automations/${id}`),
  // Emit a top-of-funnel event into the automation engine; returns matched count.
  ingestAutomationEvent: (event: {
    event_type: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    signal_type?: string;
    score?: number;
    metadata?: Record<string, unknown>;
  }) => getPlatformApi().post<{ matched_automations: number }>(`/marketing/automations/events`, event),

  // Persisted intent signals (the intent layer of the funnel).
  getIntentSignals: (params?: { signal_type?: string; strength?: string; limit?: number }) =>
    getPlatformApi().get<any[]>('/marketing/intent-signals', params as Record<string, string | number>),
  createIntentSignal: (data: {
    company?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    signal_type: string;
    score?: number;
    strength?: string;
    category?: string;
    source?: string;
    details?: string;
    metadata?: Record<string, unknown>;
  }) => getPlatformApi().post<{ id: string; strength: string; matched_automations: number }>('/marketing/intent-signals', data),
  // Batch-ingest derived signals (Visitor ID / ICP) so the intent layer
  // auto-populates; server dedupes over 7 days + fires automations.
  syncIntentSignals: (signals: Array<{ company?: string; email?: string; signal_type: string; score?: number; strength?: string; category?: string; source?: string; details?: string }>) =>
    getPlatformApi().post<{ created: number; skipped: number; matched_automations: number }>('/marketing/intent-signals/sync', { signals }),
  // Account-scoring agent: ranked accounts by composite intent (recomputes on read).
  getAccountScores: (limit = 50) =>
    getPlatformApi().get<any[]>(`/marketing/account-scores?limit=${limit}`),

  // Next-best-action agent: LLM-drafted outreach sequences behind a human-approval gate.
  getNbaRecommendations: (status?: string) =>
    getPlatformApi().get<any[]>(`/marketing/nba/recommendations${status ? `?status=${status}` : ''}`),
  generateNbaRecommendations: (body?: { company?: string; limit?: number }) =>
    getPlatformApi().post<{ created: number; recommendations: any[] }>(
      '/marketing/nba/recommendations/generate',
      body || {}
    ),
  approveNbaRecommendation: (id: string) =>
    getPlatformApi().post<{ status: string; campaign_id: string }>(`/marketing/nba/recommendations/${id}/approve`, {}),
  rejectNbaRecommendation: (id: string) =>
    getPlatformApi().post<{ status: string }>(`/marketing/nba/recommendations/${id}/reject`, {}),

  // Funnel-stage transition: route engaged (hot) accounts into the sales pipeline.
  promoteToPipeline: (body?: { company?: string; limit?: number }) =>
    getPlatformApi().post<{ created: number; opportunities: any[] }>('/marketing/pipeline/promote', body || {}),

  // Marketing notifications (funnel activity — automation fired, hot signal).
  getNotifications: (unreadOnly = false) =>
    getPlatformApi().get<{ unread: number; notifications: any[] }>(
      `/marketing/notifications${unreadOnly ? '?unread_only=true' : ''}`,
    ),
  markNotificationRead: (id: string) => getPlatformApi().post<any>(`/marketing/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => getPlatformApi().post<any>(`/marketing/notifications/read-all`, {}),

  // 2-way inbox replies to a campaign. These live at /api/campaigns/* (outside
  // the /api/platform/v1 prefix), so call them directly with the auth token.
  getCampaignResponses: async (campaignId: string): Promise<any[] | null> => {
    const token = localStorage.getItem('auth_token') || '';
    try {
      const res = await fetch(`${PLATFORM_BACKEND_URL}/api/campaigns/${campaignId}/responses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data) ? data : (data?.responses ?? []);
    } catch {
      return null;
    }
  },
  sendCampaignReply: async (
    campaignId: string,
    responseId: string,
    body: { message: string; send_via_channel: string },
  ): Promise<{ ok: boolean; status?: string; error?: string }> => {
    const token = localStorage.getItem('auth_token') || '';
    try {
      const res = await fetch(`${PLATFORM_BACKEND_URL}/api/campaigns/${campaignId}/responses/${responseId}/human-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data?.detail || `HTTP ${res.status}` };
      return { ok: true, status: data?.status };
    } catch (e: any) {
      return { ok: false, error: e?.message || 'Network error' };
    }
  },

  // Analytics
  getMarketingAnalytics: (period: '7d' | '30d' | '90d' | '1y' = '30d') =>
    getPlatformApi().get<{
      period: string;
      summary: {
        emails_sent: number;
        emails_sent_change: number;
        open_rate: number;
        open_rate_change: number;
        click_rate: number;
        click_rate_change: number;
        conversions: number;
        conversions_change: number;
        campaign_count: number;
      };
      top_campaigns: Array<{
        id: string;
        name: string;
        type: string;
        status: string;
        sent: number;
        open_rate: number;
        click_rate: number;
      }>;
    }>(`/marketing/analytics?period=${period}`),

  // Automations
  getAutomations: (params?: PaginationParams) =>
    getPlatformApi().get<Automation[]>('/marketing/automations', params as Record<string, string | number>),

  createAutomation: (data: Partial<Automation>) =>
    getPlatformApi().post<Automation>('/marketing/automations', data),

  toggleAutomation: (id: string, active: boolean) =>
    getPlatformApi().patch(`/marketing/automations/${id}`, { status: active ? 'active' : 'paused' }),

  // Contacts
  getContacts: (params?: PaginationParams & { search?: string }) =>
    getPlatformApi().get<Contact[]>('/marketing/contacts', params as Record<string, string | number>),

  getContact: (id: string) =>
    getPlatformApi().get<Contact>(`/marketing/contacts/${id}`),

  createContact: (data: Partial<Contact>) =>
    getPlatformApi().post<Contact>('/marketing/contacts', data),

  updateContact: (id: string, data: Partial<Contact>) =>
    getPlatformApi().put<Contact>(`/marketing/contacts/${id}`, data),

  importContacts: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // Special handling for file upload
    return fetch(`${API_BASE_URL}/marketing/contacts/import`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }).then(res => res.json());
  },

  // Lists (Legacy - use contactListsApi instead)
  getLists: (params?: PaginationParams) =>
    getPlatformApi().get<ContactList[]>('/marketing/lists', params as Record<string, string | number>),

  createList: (data: Partial<ContactList>) =>
    getPlatformApi().post<ContactList>('/marketing/lists', data),
};

export interface ContributorPr {
  repo: string;
  repo_short: 'be' | 'fe';
  number: number;
  title: string;
  url: string;
  author: string;
  draft: boolean;
  base: string;
  branch: string;
  created_at: string;
  updated_at?: string;
  merged_at?: string | null;
  jira_key: string | null;
  ci_state?: 'success' | 'failed' | 'pending' | 'none';
  ci_failing?: string[];
  ci_error?: string | null;
  live?: boolean | null;
}

export interface ContributorPrsResponse {
  configured: boolean;
  approver: string;
  jira_browse_base: string;
  merged_window_days: number;
  open: ContributorPr[];
  merged: ContributorPr[];
  // Repos GitHub refused to list (e.g. the service token cannot see them).
  errors?: string[];
}

export interface ContributorPrRun {
  name: string;
  status: string | null;
  conclusion: string | null;
  url: string | null;
}

export interface ContributorPrDetail extends ContributorPr {
  body: string;
  state: string;
  merged: boolean;
  mergeable: boolean | null;
  mergeable_state: string | null;
  additions: number;
  deletions: number;
  changed_files: number;
  commit_count: number;
  files: { filename: string; status: string; additions: number; deletions: number }[];
  files_truncated: boolean;
  commits: { sha: string; message: string; author: string | null; date: string | null }[];
  reviews: { user: string | null; state: string; body: string; submitted_at: string | null }[];
  ci: { state: 'success' | 'failed' | 'pending' | 'none' | 'unknown'; runs: ContributorPrRun[]; error: string | null };
  risk_flags: { message: string; files: string[] }[];
  jira: { key: string; summary?: string; status?: string; assignee?: string; error?: string } | null;
}

export interface ContributorPrActionResult {
  ok?: boolean;
  merged?: boolean;
  already?: boolean;
  reason?: string;
  ci_state?: string;
  jira_key?: string | null;
  jira_done?: boolean;
  jira_created?: boolean;
  jira_logged?: boolean;
  jira_error?: string;
  state?: string;
}

/** Query params for `GET /agents/rca/log` (TRUS-687 adds the multi-value axes, paging and group_by). */
export interface RcaLogParams {
  status?: string;
  verdict?: string;
  journey?: string;
  label?: string;
  q?: string; // ticket key, TKT- number or title (≤200 chars)
  priority?: string; // comma-separated Jira priority names, or 'none'
  due?: string; // overdue | due_soon | has_due | no_due
  org?: string; // cbo ids, or 'none'
  origin?: string; // cs_widget | jira_manual | direct_session | pipeline_split | observability | pr_review | cs_ticket | none
  reporter?: string; // user ids, 'me' or 'none'
  assignee?: string; // user ids, 'me' or 'none'
  issue_type?: string;
  jira_category?: string; // new | indeterminate | done
  has_pr?: string; // 'true' | 'false'
  reviewed_by?: string; // a user id or 'me'
  sort?: string; // updated | created | priority | due | status | org | origin | assignee
  group_by?: string; // org | origin | verdict | status | journey | assignee | reporter | epic
  group_limit?: number;
  cursor?: string;
  limit?: number;
}
const RCA_LOG_PARAM_KEYS: (keyof RcaLogParams)[] = [
  'status', 'verdict', 'journey', 'label', 'q', 'priority', 'due', 'org', 'origin', 'reporter', 'assignee',
  'issue_type', 'jira_category', 'has_pr', 'reviewed_by', 'sort', 'group_by', 'group_limit', 'cursor', 'limit',
];

// Agent Control Plane (Phase 1, read-only): the agent catalog, recent runs from
// the central ai_tasks tracker, the dashboard summary, and the human-approval
// gate queue (NBA drafts + RCA fixes).
export const agentsApi = {
  getRegistry: (product?: string) =>
    getPlatformApi().get<any[]>(`/agents/registry${product ? `?product=${product}` : ''}`),
  getRuns: (params?: { limit?: number; task_type?: string; product?: string }) =>
    getPlatformApi().get<any[]>('/agents/runs', params as Record<string, string | number>),
  getSummary: (product?: string) =>
    getPlatformApi().get<{ runs_24h: number; failed_24h: number; cost_24h: number; gated: number; gated_nba: number; gated_rca: number; queued_rca?: number; can_control?: boolean }>(
      '/agents/summary',
      product ? { product } : undefined
    ),
  getGated: (product?: string) =>
    getPlatformApi().get<{ items: any[]; count: number }>(
      '/agents/gated',
      product ? { product } : undefined
    ),
  // All filters are applied server-side so the fetched window is drawn from the matching
  // set, not sliced from the newest-N-of-everything. TRUS-687: every axis takes comma-
  // separated values (OR within an axis, AND across axes). Paging is keyset: omit `limit`
  // and pass the previous response's `next_cursor` as `cursor`. An older backend ignores
  // the params it doesn't know and sends no `next_cursor`; the console then falls back to
  // one window of `limit` rows (see AgentControlPage).
  getRcaLog: (params?: RcaLogParams) => {
    const qs = new URLSearchParams();
    for (const key of RCA_LOG_PARAM_KEYS) {
      const v = params?.[key];
      if (v === undefined || v === null || v === '') continue;
      qs.set(key, String(v));
    }
    const query = qs.toString();
    return getPlatformApi().get<any>(`/agents/rca/log${query ? `?${query}` : ''}`);
  },
  // TRUS-687 saved views (per user). GET also returns the built-ins (`builtin: true`).
  // params = the filter/sort/group object: ids and enum values, plus `q`.
  listRcaViews: () => getPlatformApi().get<any>('/agents/rca/views'),
  createRcaView: (body: { name: string; params: object }) =>
    getPlatformApi().post<any>('/agents/rca/views', body),
  updateRcaView: (id: string, body: { name?: string; params?: object }) =>
    getPlatformApi().put<any>(`/agents/rca/views/${encodeURIComponent(id)}`, body),
  deleteRcaView: (id: string) => getPlatformApi().delete<any>(`/agents/rca/views/${encodeURIComponent(id)}`),
  // Original ticket context (description + screenshots) for a gate/log item.
  getRcaContext: (id: string) =>
    getPlatformApi().get<any>(`/agents/rca/${encodeURIComponent(id)}/context`),
  // Resolution journey (implementation arc: drafted -> CI -> approved -> merged -> deployed).
  getRcaJourney: (id: string) =>
    getPlatformApi().get<any>(`/agents/rca/${encodeURIComponent(id)}/journey`),
  // Integrated activity timeline — one chronological thread merging the durable
  // rca_job_events log (creator request, agent analysis, proposed changes, reviewer
  // context/feedback, held question/replies, resolution milestones) with the live
  // Jira/cs comment thread. Each entry carries its own timestamp.
  getRcaTimeline: (id: string) =>
    getPlatformApi().get<any>(`/agents/rca/${encodeURIComponent(id)}/timeline`),
  // Controls (Phase 2)
  updateAgent: (key: string, body: { status?: string; budget_usd?: number }) =>
    getPlatformApi().patch<{ key: string; status: string; budget_usd: number | null }>(`/agents/${key}`, body),
  runAgent: (key: string) =>
    getPlatformApi().post<{ queued: boolean; task: string; task_id: string }>(`/agents/${encodeURIComponent(key)}/run`, {}),
  // approve/reject/done are the base gate actions. The product-decision actions
  // (approve-spec / defer / decline) apply to a FEATURE held for a product decision:
  //   approve-spec → product-approved, ready for a SEPARATE build session (not the agent)
  //   defer        → parked in the product backlog (explicit dismissal, not auto-close)
  //   decline      → declined as out of scope (terminal; reason posted to the creator)
  rcaGateAction: (
    jobId: string,
    action: 'approve' | 'reject' | 'done' | 'approve-spec' | 'defer' | 'decline',
  ) => getPlatformApi().post<{ status: string }>(`/agents/rca/${jobId}/${action}`, {}),
  // Close a ticket RESOLVED OUTSIDE the pipeline (fixed in a separate PR / config / already
  // handled): sets the job to 'done' + best-effort transitions the linked Jira issue to Done.
  // Distinct from approve (which re-queues a build) and dismiss (which reads as won't-fix).
  rcaResolve: (jobId: string, note?: string) =>
    getPlatformApi().post<{ status: string; jira_transitioned: boolean }>(
      `/agents/rca/${encodeURIComponent(jobId)}/resolve`,
      note ? { note } : {},
    ),
  // Bulk close: mark many selected tickets 'done' at once + best-effort transition each
  // linked Jira issue to Done. Skips already-terminal / out-of-scope ids (returns them).
  rcaBulkAction: (ids: Array<string | number>, action: 'done' = 'done', note?: string) =>
    getPlatformApi().post<{
      action: string;
      requested: number;
      done: number[];
      jira_closed: number;
      skipped: number[];
    }>(`/agents/rca/bulk-action`, { ids: ids.map((i) => Number(i)), action, ...(note ? { note } : {}) }),
  // Persist a corrected verdict WITHOUT re-running the agent — the sync guard for the
  // Jira→rca_jobs drift that stranded TRUS-179 (reclassified in Jira only, so the DB
  // kept the stale verdict and the console showed Approve instead of the hand-off).
  reclassifyRca: (id: string, verdict: string) =>
    getPlatformApi().post<{ status: string; verdict: string; ticket: string }>(
      `/agents/rca/${encodeURIComponent(id)}/reclassify`,
      { verdict },
    ),
  // Set one part's status on a multi-part OPERATIONAL ticket's progress trail
  // (done | needs_build | skipped | pending).
  rcaUpdatePart: (id: string, n: number, status: string, detail?: string) =>
    getPlatformApi().post<{ job_id: string; parts: any[] }>(`/agents/rca/${encodeURIComponent(id)}/part`, {
      n,
      status,
      ...(detail ? { detail } : {}),
    }),
  // Mark an OPERATIONAL job done WITH the result of a manual action (records rca_summary,
  // unlike the plain 'done' gate action which drops it).
  rcaCompleteManual: (id: string, result: string) =>
    getPlatformApi().post<{ status: string; external_key: string }>(
      `/agents/rca/${encodeURIComponent(id)}/complete-manual`,
      { result },
    ),
  // Console "Run now" for a field-config OPERATIONAL job: apply the com_fields change
  // (type/options/label/mandatory/hidden) server-side AND mark the job done with results.
  rcaRunFieldConfig: (
    id: string,
    body: {
      cbo_id: number;
      field_name?: string;
      field_id?: number;
      type?: string;
      field_options?: any[];
      friendly_name?: string;
      mandatory?: boolean;
      hidden?: boolean;
    },
  ) => getPlatformApi().post<{ status: string; field_id: number }>(`/agents/rca/${encodeURIComponent(id)}/run-field-config`, body),
  // Review & deploy tab: contributor PRs into master/main awaiting the approver,
  // plus contributor PRs merged in the last week with their live status.
  // refresh=true bypasses the API's 30 s cache (the tab's Refresh button).
  getContributorPrs: (refresh = false) =>
    getPlatformApi().get<ContributorPrsResponse>('/agents/contributor-prs', refresh ? { refresh: 'true' } : undefined),
  // Give every open contributor PR without a TRUS key a Jira ticket now (also runs every 10 min).
  getContributorPr: (repoShort: string, number: number, refresh = false) =>
    getPlatformApi().get<ContributorPrDetail>(
      `/agents/contributor-prs/${repoShort}/${number}`,
      refresh ? { refresh: 'true' } : undefined,
    ),
  // Create the PR's Jira ticket now instead of waiting for the 10-minute sync.
  ticketContributorPr: (repoShort: string, number: number) =>
    getPlatformApi().post<ContributorPrActionResult>(`/agents/contributor-prs/${repoShort}/${number}/ticket`, {}),
  readyContributorPr: (repoShort: string, number: number) =>
    getPlatformApi().post<ContributorPrActionResult>(`/agents/contributor-prs/${repoShort}/${number}/ready`, {}),
  reviewContributorPr: (repoShort: string, number: number, event: 'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT', body?: string) =>
    getPlatformApi().post<ContributorPrActionResult>(`/agents/contributor-prs/${repoShort}/${number}/review`, { event, body }),
  // Merging into master/main IS the production deploy. force overrides the CI guard.
  mergeContributorPr: (repoShort: string, number: number, force = false) =>
    getPlatformApi().post<ContributorPrActionResult>(`/agents/contributor-prs/${repoShort}/${number}/merge`, { method: 'squash', force }),
  syncContributorPrs: () =>
    getPlatformApi().post<{ ok: boolean; open_prs: number; counts: Record<string, number> }>(
      '/agents/contributor-prs/sync',
      {},
    ),
  // Merge a ticket's drafted PR from the console (control-gated on the API).
  mergeRca: (id: string, method?: string) =>
    getPlatformApi().post<any>(`/agents/rca/${encodeURIComponent(id)}/merge`, method ? { method } : {}),
  // Merge-conflict fallback #1: ask GitHub to merge the latest base into a stale PR head.
  updateRcaBranch: (id: string) =>
    getPlatformApi().post<{ updated: boolean; message?: string; reason?: string; needs_agent?: boolean }>(
      `/agents/rca/${encodeURIComponent(id)}/update-branch`,
      {},
    ),
  // Hand a PR the console can't merge back to the agent to FIX (merge conflict OR red CI).
  // reason describes the blocker so the agent fixes the right thing.
  resolveRcaConflicts: (id: string, reason?: string) =>
    getPlatformApi().post<{ requeued: boolean; ticket: string }>(
      `/agents/rca/${encodeURIComponent(id)}/resolve-conflicts`,
      reason ? { reason } : {},
    ),
  // Pre-development intake review → approved for implementation. The backend dispatches like
  // Approve: the agent builds it, the service executor runs it, or an already-drafted PR goes
  // to code review. note (optional) is passed to the building agent as reviewer context.
  rcaIntakeApprove: (id: string, note?: string) =>
    getPlatformApi().post<{ status: string; ticket: string }>(
      `/agents/rca/${encodeURIComponent(id)}/intake-approve`,
      note ? { note } : {},
    ),
  // TRUS-611: the ticket's plain-language summary (What's wrong / found / recommend / affects).
  // Returns the stored one, or generates + caches it from the analysis when there is none
  // (or when regenerate=true).
  rcaBusinessSummary: (id: string, regenerate = false) =>
    getPlatformApi().post<{ business_summary: string; business_summary_source: string; business_summary_at: string | null }>(
      `/agents/rca/${encodeURIComponent(id)}/business-summary`,
      { regenerate },
    ),
  // Why the console total differs from the Jira project count: every Jira issue with no
  // rca_job, broken down by issue type (pre-pipeline history, Epics, Sub-tasks, …).
  getRcaCoverage: (project = 'TRUS') =>
    getPlatformApi().get<{
      project: string;
      jira_total: number;
      jira_by_type: Record<string, number>;
      rca_total: number;
      rca_by_source: Record<string, number>;
      jira_tracked: number;
      jira_missing: number;
      jira_missing_by_type: Record<string, number>;
      missing_created_before_first_job: number;
      first_job_enqueued_at: string | null;
      missing: Array<{ key: string; type: string; created: string | null; status: string | null }>;
    }>(`/agents/rca/coverage?project=${encodeURIComponent(project)}`),
  // Re-queue a ticket with added reviewer context. implement=false → re-triage;
  // implement=true → confirm a product decision and have the agent build it.
  reanalyzeRca: (id: string, context: string, implement = false) =>
    getPlatformApi().post<{ status: string }>(`/agents/rca/${encodeURIComponent(id)}/reanalyze`, { context, implement }),
  // Hold a ticket awaiting a response from the creator; posts the question on Jira + notifies admins.
  // returnTo: audience to hand the ticket back to once they answer — 'restore' keeps the
  // admin/product decision the question was released FROM (so the answer comes back into that
  // decision instead of re-triaging the ticket), 'none' for a plain ask with nothing parked.
  holdRca: (id: string, question: string, returnTo?: 'restore' | 'admin' | 'product' | 'intake' | 'none') =>
    getPlatformApi().post<any>(
      `/agents/rca/${encodeURIComponent(id)}/hold`,
      returnTo ? { question, return_to: returnTo } : { question },
    ),
  // Record the creator's answer to a released question. then='restore' hands the parked
  // admin/product decision back with the answer attached; 'reanalyze' re-queues the agent.
  creatorReplyRca: (id: string, reply: string, then: 'restore' | 'reanalyze' = 'restore') =>
    getPlatformApi().post<{ status: string; ticket: string; held_for: string | null }>(
      `/agents/rca/${encodeURIComponent(id)}/creator-reply`,
      { reply, then },
    ),
  // Post a reviewer reply back to the ticket's Jira issue (notifies reporter/assignee/watchers).
  replyRca: (id: string, text: string) =>
    getPlatformApi().post<{ posted: boolean; ticket: string }>(`/agents/rca/${encodeURIComponent(id)}/reply`, { text }),
  // Admin override: lift a held ticket. mode='requeue' → agent builds/executes; 'review' → back to gate.
  // Release a job stuck in 'claimed'/'executing' — a run that died mid-job leaves the
  // console reading "In Progress" forever and no other control accepts that status.
  requeueRca: (id: string) =>
    getPlatformApi().post<{ status: string; ticket: string; previous_status: string }>(
      `/agents/rca/${encodeURIComponent(id)}/requeue`,
      {},
    ),

  unholdRca: (id: string, mode: 'requeue' | 'review' = 'requeue') =>
    getPlatformApi().post<{ status: string; ticket: string }>(`/agents/rca/${encodeURIComponent(id)}/unhold`, { mode }),
  // Epics: list Epics the pipeline has seen (with per-Epic mode + child rollup), and set
  // an Epic's mode ('auto' triages + releases held children; 'hold' parks them).
  listEpics: () =>
    getPlatformApi().get<{ epics: any[] }>(`/agents/rca/epics`),
  setEpicMode: (epicKey: string, mode: 'auto' | 'hold' | 'closed') =>
    getPlatformApi().post<{ epic_key: string; mode: string; released: number; closed_children?: number }>(
      `/agents/rca/epics/${encodeURIComponent(epicKey)}/mode`,
      { mode },
    ),
  // Fire the RCA cloud routine on demand (don't wait for the hourly run).
  runRcaNow: () => getPlatformApi().post<{ fired: boolean; session_url?: string }>(`/agents/rca/run-now`, {}),
  // The visible catalog of what "Hand off to agent" can do (svc-* tools + code PRs) + can't.
  getCapabilities: () => getPlatformApi().get<{ groups: any[]; cannot: string[] }>(`/agents/rca/capabilities`),
  setAgentSchedule: (key: string, body: { cron_expr?: string; enabled?: boolean }) =>
    getPlatformApi().patch<{ cron_expr: string; enabled: boolean }>(`/agents/${encodeURIComponent(key)}/schedule`, body),
  // Versioned agent policy (the executor prompt) — view + edit behind a gate.
  getAgentPolicy: (key: string) =>
    getPlatformApi().get<any>(`/agents/${encodeURIComponent(key)}/policy`),
  setAgentPolicy: (key: string, body: { policy: string; note?: string }) =>
    getPlatformApi().patch<{ key: string; version: number }>(`/agents/${encodeURIComponent(key)}/policy`, body),
  createAgent: (body: {
    display_name: string;
    trigger_type: string;
    trigger_config?: Record<string, any>;
    action_type: string;
    action_config?: Record<string, any>;
  }) => getPlatformApi().post<{ key: string; automation_id: string; display_name: string }>('/agents', body),
  createCodedAgent: (body: {
    display_name: string;
    task_target: string;
    cron_expr: string;
    product?: string;
    description?: string;
    queue?: string;
  }) => getPlatformApi().post<{ key: string; task_target: string; cron_expr: string }>('/agents/coded', body),
  createLlmAgent: (body: {
    display_name: string;
    prompt: string;
    cron_expr: string;
    model?: string;
    gate_policy?: string;
    product?: string;
  }) => getPlatformApi().post<{ key: string; cron_expr: string; gate_policy: string }>('/agents/llm', body),
  reviewAgentOutput: (id: string, action: 'approve' | 'reject') =>
    getPlatformApi().post<{ status: string }>(`/agents/output/${id}/${action}`, {}),
  // Phase 4 scope governance — per-agency/per-program overrides.
  getAgentScopes: (key: string) =>
    getPlatformApi().get<{ global: any; overrides: any[] }>(`/agents/${encodeURIComponent(key)}/scopes`),
  setAgentScope: (key: string, body: { scope_level: string; cbo_id?: number; program_id?: number; status?: string; budget_usd?: number }) =>
    getPlatformApi().patch<any>(`/agents/${encodeURIComponent(key)}/scope`, body),
  getScopeOptions: () =>
    getPlatformApi().get<{ orgs: any[]; programs: any[]; can_scope_all: boolean }>('/agents/scope-options'),
  // ── Wave 1: shared marketing/sales tool catalogue (both the console and voice AI call these) ──
  getToolCatalogue: () =>
    getPlatformApi().get<{ groups: Record<string, ToolSpec[]>; total: number }>('/agent-tools'),
  invokeTool: (name: string, body: { args?: Record<string, any>; cbo_id?: number; approved?: boolean }) =>
    getPlatformApi().post<any>(`/agent-tools/${encodeURIComponent(name)}`, body),
  // ── Wave 1: voice conversation layer — place an agent-driven call, list sessions ──
  startVoiceCall: (body: {
    agent_key: string;
    to_number: string;
    cbo_id?: number;
    lang?: string;
    context?: Record<string, any>;
  }) => getPlatformApi().post<any>('/agent-voice/start', body),
  listVoiceSessions: (limit?: number) =>
    getPlatformApi().get<{ sessions: any[] }>('/agent-voice/sessions', limit ? { limit } : undefined),
  // ── Wave 2: run an orchestrator/LLM agent on an instruction (e.g. the web studio
  // director builds a draft page). Returns summary + steps + pending approvals. ──
  runAgentOrchestrator: (agentKey: string, body: { instruction: string; cbo_id?: number; approved?: boolean }) =>
    getPlatformApi().post<any>(`/agent-run/${encodeURIComponent(agentKey)}`, body),
  // ── Mic v2: mint an in-browser streaming-voice session. `streaming` is false when
  // Deepgram isn't configured (client then falls back to browser speech). ──
  createBrowserVoiceSession: (body: { agent_key: string; cbo_id?: number; lang?: string; context?: Record<string, any> }) =>
    getPlatformApi().post<{ session_id: string; streaming: boolean }>('/agent-voice/browser-session', body),
};

export interface ToolSpec {
  name: string;
  group: string;
  summary: string;
  gate: 'autonomous' | 'human_approval';
  params: Record<string, string>;
}

// ============================================================================
// CONTACT LISTS API (for Marketing Automation audience targeting)
// ============================================================================

export interface ContactListFull {
  id: string;
  name: string;
  description?: string;
  list_type: 'static' | 'dynamic';
  member_count: number;
  filter_criteria?: {
    lifecycle_stage?: string;
    status?: string;
    min_score?: number;
    max_score?: number;
    tags?: string[];
    do_not_email?: boolean;
  };
  is_active: boolean;
  cbo_id?: number;
  created_at: string;
  updated_at?: string;
}

export interface ContactListMember {
  id: string;
  contact_id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  lifecycle_stage?: string;
  score: number;
  added_at: string;
  source: string;
}

export interface ContactListCreate {
  name: string;
  description?: string;
  list_type?: 'static' | 'dynamic';
  filter_criteria?: ContactListFull['filter_criteria'];
}

export interface ContactListUpdate {
  name?: string;
  description?: string;
  filter_criteria?: ContactListFull['filter_criteria'];
  is_active?: boolean;
}

// Contact Lists API - uses /api/platform/contact-lists endpoint
// Uses PLATFORM_BACKEND_URL directly since contact-lists is NOT under /v1
const CONTACT_LISTS_BASE_URL = `${PLATFORM_BACKEND_URL}/api/platform/contact-lists`;

// Helper function to get auth headers for contact lists API
const getContactListsHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
  'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
});

export const contactListsApi = {
  // List all contact lists
  listContactLists: async (params?: { list_type?: string; is_active?: boolean; search?: string }): Promise<ContactListFull[]> => {
    const queryParams = new URLSearchParams();
    if (params?.list_type) queryParams.append('list_type', params.list_type);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = queryString ? `${CONTACT_LISTS_BASE_URL}?${queryString}` : CONTACT_LISTS_BASE_URL;

    const response = await fetch(url, {
      method: 'GET',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch contact lists:', response.status, errorText);
      throw new Error(`Failed to fetch contact lists: ${response.status}`);
    }

    return response.json();
  },

  // Get a single contact list
  getContactList: async (listId: string): Promise<ContactListFull> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}`, {
      method: 'GET',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch contact list');
    }

    return response.json();
  },

  // Create a new contact list
  createContactList: async (data: ContactListCreate): Promise<ContactListFull> => {
    const response = await fetch(CONTACT_LISTS_BASE_URL, {
      method: 'POST',
      headers: getContactListsHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create contact list');
    }

    return response.json();
  },

  // Spine bridge: import raw contacts from a lead source into a list (upserts
  // ma_contacts deduped by email, then adds to the list).
  importContacts: async (
    listId: string,
    contacts: Array<{ email?: string; first_name?: string; last_name?: string; company?: string; phone?: string }>,
    source = 'lead_builder',
  ): Promise<{ added: number; skipped: number; errors: number }> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}/import`, {
      method: 'POST',
      headers: getContactListsHeaders(),
      credentials: 'include',
      body: JSON.stringify({ contacts, source }),
    });
    if (!response.ok) {
      throw new Error('Failed to import contacts');
    }
    return response.json();
  },

  // Update a contact list
  updateContactList: async (listId: string, data: ContactListUpdate): Promise<ContactListFull> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}`, {
      method: 'PUT',
      headers: getContactListsHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update contact list');
    }

    return response.json();
  },

  // Delete a contact list
  deleteContactList: async (listId: string): Promise<void> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}`, {
      method: 'DELETE',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to delete contact list');
    }
  },

  // Get members of a contact list
  getListMembers: async (listId: string, limit = 100, offset = 0): Promise<ContactListMember[]> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}/members?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch list members');
    }

    return response.json();
  },

  // Add members to a contact list
  addMembers: async (listId: string, contactIds: string[]): Promise<{ added: number; skipped: number; errors: number }> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}/members`, {
      method: 'POST',
      headers: getContactListsHeaders(),
      credentials: 'include',
      body: JSON.stringify({ contact_ids: contactIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to add members to list');
    }

    return response.json();
  },

  // Remove a member from a contact list
  removeMember: async (listId: string, contactId: string): Promise<void> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}/members/${contactId}`, {
      method: 'DELETE',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to remove member from list');
    }
  },

  // Refresh a dynamic contact list
  refreshDynamicList: async (listId: string): Promise<{ success: boolean; member_count: number }> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/${listId}/refresh`, {
      method: 'POST',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to refresh dynamic list');
    }

    return response.json();
  },

  // Preview contacts that would match dynamic filter criteria
  previewDynamicList: async (filterCriteria: {
    lifecycle_stage?: string;
    status?: string;
    min_score?: number;
    max_score?: number;
    do_not_email?: boolean;
  }, limit = 50): Promise<{
    total_count: number;
    preview_contacts: Array<{
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      company_name?: string;
      lifecycle_stage?: string;
      status?: string;
      score: number;
    }>;
    filter_criteria: Record<string, unknown>;
  }> => {
    const response = await fetch(`${CONTACT_LISTS_BASE_URL}/preview?limit=${limit}`, {
      method: 'POST',
      headers: getContactListsHeaders(),
      credentials: 'include',
      body: JSON.stringify(filterCriteria),
    });

    if (!response.ok) {
      throw new Error('Failed to preview dynamic list');
    }

    return response.json();
  },

  // Get available contacts for static list selection
  getAvailableContacts: async (params?: {
    search?: string;
    exclude_list_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    contacts: Array<{
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      company_name?: string;
      lifecycle_stage?: string;
      status?: string;
      score: number;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.exclude_list_id) queryParams.append('exclude_list_id', params.exclude_list_id);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const queryString = queryParams.toString();
    const url = queryString
      ? `${CONTACT_LISTS_BASE_URL}/available-contacts?${queryString}`
      : `${CONTACT_LISTS_BASE_URL}/available-contacts`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getContactListsHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get available contacts');
    }

    return response.json();
  },
};

// ============================================================================
// SALES API
// ============================================================================

export interface Lead {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  source: string;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  assignedTo?: string;
  score: number;
  createdAt: string;
}

export interface Opportunity {
  id: string;
  name: string;
  company: string;
  value: number;
  stage: string;
  probability: number;
  expectedCloseDate: string;
  assignedTo?: string;
  contacts: string[];
  createdAt: string;
}

export interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'task' | 'note';
  title: string;
  description?: string;
  relatedTo: { type: string; id: string };
  dueDate?: string;
  completedAt?: string;
  assignedTo?: string;
  createdAt: string;
}

export const salesApi = {
  // Leads
  getLeads: (params?: PaginationParams & { status?: string }) =>
    getPlatformApi().get<Lead[]>('/sales/leads', params as Record<string, string | number>),

  createLead: (data: Partial<Lead>) =>
    getPlatformApi().post<Lead>('/sales/leads', data),

  updateLead: (id: string, data: Partial<Lead>) =>
    getPlatformApi().put<Lead>(`/sales/leads/${id}`, data),

  convertLead: (id: string, opportunityData: Partial<Opportunity>) =>
    getPlatformApi().post(`/sales/leads/${id}/convert`, opportunityData),

  // Opportunities
  getOpportunities: (params?: PaginationParams & { stage?: string }) =>
    getPlatformApi().get<Opportunity[]>('/sales/opportunities', params as Record<string, string | number>),

  getOpportunity: (id: string) =>
    getPlatformApi().get<Opportunity>(`/sales/opportunities/${id}`),

  createOpportunity: (data: Partial<Opportunity>) =>
    getPlatformApi().post<Opportunity>('/sales/opportunities', data),

  updateOpportunity: (id: string, data: Partial<Opportunity>) =>
    getPlatformApi().put<Opportunity>(`/sales/opportunities/${id}`, data),

  moveOpportunityStage: (id: string, stage: string) =>
    getPlatformApi().patch(`/sales/opportunities/${id}`, { stage }),

  // Activities
  getActivities: (params?: PaginationParams & { type?: string }) =>
    getPlatformApi().get<Activity[]>('/sales/activities', params as Record<string, string | number>),

  createActivity: (data: Partial<Activity>) =>
    getPlatformApi().post<Activity>('/sales/activities', data),

  completeActivity: (id: string) =>
    getPlatformApi().patch(`/sales/activities/${id}`, { completedAt: new Date().toISOString() }),

  // Pipeline Stats
  getPipelineStats: () =>
    getPlatformApi().get<{ stage: string; count: number; value: number }[]>('/sales/pipeline/stats'),

  getForecast: (period: 'month' | 'quarter' | 'year') =>
    getPlatformApi().get<{ period: string; forecast: number; closed: number }[]>(`/sales/forecast?period=${period}`),
};

// ============================================================================
// REVOPS API
// ============================================================================

export interface Subscription {
  id: string;
  customerId: string;
  customerName: string;
  planId: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'cancelled';
  mrr: number;
  startDate: string;
  currentPeriodEnd: string;
  cancelledAt?: string;
}

export interface Invoice {
  id: string;
  customerId: string;
  customerName: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  paidAt?: string;
  createdAt: string;
}

export const revOpsApi = {
  // Subscriptions
  getSubscriptions: (params?: PaginationParams & { status?: string }) =>
    getPlatformApi().get<Subscription[]>('/revops/subscriptions', params as Record<string, string | number>),

  getSubscription: (id: string) =>
    getPlatformApi().get<Subscription>(`/revops/subscriptions/${id}`),

  cancelSubscription: (id: string) =>
    getPlatformApi().post(`/revops/subscriptions/${id}/cancel`, {}),

  // Invoices
  getInvoices: (params?: PaginationParams & { status?: string }) =>
    getPlatformApi().get<Invoice[]>('/revops/invoices', params as Record<string, string | number>),

  createInvoice: (data: Partial<Invoice>) =>
    getPlatformApi().post<Invoice>('/revops/invoices', data),

  sendInvoice: (id: string) =>
    getPlatformApi().post(`/revops/invoices/${id}/send`, {}),

  markInvoicePaid: (id: string) =>
    getPlatformApi().patch(`/revops/invoices/${id}`, { status: 'paid', paidAt: new Date().toISOString() }),

  // Metrics
  getRevenueMetrics: (period: 'month' | 'quarter' | 'year') =>
    getPlatformApi().get<{ mrr: number; arr: number; churn: number; growth: number }>(`/revops/metrics?period=${period}`),
};

// ============================================================================
// SUPPORT API
// ============================================================================

export interface Ticket {
  id: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  customerId: string;
  customerName: string;
  assignedTo?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeBaseArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  category: string;
  status: 'draft' | 'published';
  views: number;
  helpful: number;
  notHelpful: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerHealth {
  id: string;
  customerId: string;
  customerName: string;
  score: number;
  trend: 'up' | 'down' | 'stable';
  riskLevel: 'low' | 'medium' | 'high';
  lastActivityAt: string;
  factors: { name: string; score: number; weight: number }[];
}

export const supportApi = {
  // Tickets
  getTickets: (params?: PaginationParams & { status?: string; priority?: string }) =>
    getPlatformApi().get<Ticket[]>('/support/tickets', params as Record<string, string | number>),

  getTicket: (id: string) =>
    getPlatformApi().get<Ticket>(`/support/tickets/${id}`),

  createTicket: (data: Partial<Ticket>) =>
    getPlatformApi().post<Ticket>('/support/tickets', data),

  updateTicket: (id: string, data: Partial<Ticket>) =>
    getPlatformApi().put<Ticket>(`/support/tickets/${id}`, data),

  assignTicket: (id: string, userId: string) =>
    getPlatformApi().patch(`/support/tickets/${id}`, { assignedTo: userId }),

  // Knowledge Base
  getArticles: (params?: PaginationParams & { category?: string; search?: string }) =>
    getPlatformApi().get<KnowledgeBaseArticle[]>('/support/kb/articles', params as Record<string, string | number>),

  getArticle: (id: string) =>
    getPlatformApi().get<KnowledgeBaseArticle>(`/support/kb/articles/${id}`),

  createArticle: (data: Partial<KnowledgeBaseArticle>) =>
    getPlatformApi().post<KnowledgeBaseArticle>('/support/kb/articles', data),

  updateArticle: (id: string, data: Partial<KnowledgeBaseArticle>) =>
    getPlatformApi().put<KnowledgeBaseArticle>(`/support/kb/articles/${id}`, data),

  // Health Scores
  getHealthScores: (params?: PaginationParams & { riskLevel?: string }) =>
    getPlatformApi().get<CustomerHealth[]>('/support/health-scores', params as Record<string, string | number>),

  getCustomerHealth: (customerId: string) =>
    getPlatformApi().get<CustomerHealth>(`/support/health-scores/${customerId}`),
};

// ============================================================================
// INTENT INTELLIGENCE API
// ============================================================================

export interface BuyingSignal {
  id: string;
  company: string;
  signalType: 'website_visit' | 'content_download' | 'pricing_page' | 'demo_request' | 'competitor_research' | 'job_posting';
  strength: 'hot' | 'warm' | 'cold';
  category: string;
  source: string;
  details: string;
  timestamp: string;
}

export interface VendorMatch {
  id: string;
  signalId: string;
  vendorId: string;
  matchScore: number;
  matchReasons: string[];
  status: 'pending' | 'contacted' | 'qualified' | 'disqualified';
  assignedTo?: string;
  createdAt: string;
}

export interface Vendor {
  id: string;
  name: string;
  category: string;
  capabilities: string[];
  rating: number;
  activeDeals: number;
  createdAt: string;
}

export interface MatchingRule {
  id: string;
  name: string;
  trigger: string;
  conditions: string[];
  actions: string[];
  status: 'active' | 'paused';
  matches: number;
  createdAt: string;
}

export const intentApi = {
  // Signals
  getSignals: (params?: PaginationParams & { strength?: string; signalType?: string }) =>
    getPlatformApi().get<BuyingSignal[]>('/intent/signals', params as Record<string, string | number>),

  getSignal: (id: string) =>
    getPlatformApi().get<BuyingSignal>(`/intent/signals/${id}`),

  // Matches
  getMatches: (params?: PaginationParams & { status?: string }) =>
    getPlatformApi().get<VendorMatch[]>('/intent/matches', params as Record<string, string | number>),

  createMatch: (data: { signalId: string; vendorId: string; notes?: string }) =>
    getPlatformApi().post<VendorMatch>('/intent/matches', data),

  updateMatchStatus: (id: string, status: VendorMatch['status']) =>
    getPlatformApi().patch(`/intent/matches/${id}`, { status }),

  assignMatch: (id: string, userId: string) =>
    getPlatformApi().patch(`/intent/matches/${id}`, { assignedTo: userId }),

  // Vendors
  getVendors: (params?: PaginationParams & { category?: string }) =>
    getPlatformApi().get<Vendor[]>('/intent/vendors', params as Record<string, string | number>),

  createVendor: (data: Partial<Vendor>) =>
    getPlatformApi().post<Vendor>('/intent/vendors', data),

  updateVendor: (id: string, data: Partial<Vendor>) =>
    getPlatformApi().put<Vendor>(`/intent/vendors/${id}`, data),

  // Rules
  getRules: (params?: PaginationParams) =>
    getPlatformApi().get<MatchingRule[]>('/intent/rules', params as Record<string, string | number>),

  createRule: (data: Partial<MatchingRule>) =>
    getPlatformApi().post<MatchingRule>('/intent/rules', data),

  updateRule: (id: string, data: Partial<MatchingRule>) =>
    getPlatformApi().put<MatchingRule>(`/intent/rules/${id}`, data),

  toggleRule: (id: string, active: boolean) =>
    getPlatformApi().patch(`/intent/rules/${id}`, { status: active ? 'active' : 'paused' }),

  // Analytics
  getIntentAnalytics: (period: 'week' | 'month' | 'quarter') =>
    getPlatformApi().get<{
      totalSignals: number;
      matchRate: number;
      conversionRate: number;
      signalsByType: { type: string; count: number }[];
      matchesByIndustry: { industry: string; matches: number; conversion: number }[];
    }>(`/intent/analytics?period=${period}`),
};

// ============================================================================
// CONTACTS DIRECTORY API (Enhanced)
// ============================================================================

export type LifecycleStage = 'contact' | 'subscriber' | 'lead' | 'mql' | 'sql' | 'opportunity' | 'customer' | 'evangelist';
export type ContactStatus = 'active' | 'unsubscribed' | 'bounced' | 'spam_complaint' | 'converted';

export interface DirectoryContact {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  mobile_phone?: string;
  company_name?: string;
  job_title?: string;
  department?: string;

  // Organization link (CBO)
  cbo_id?: number;
  organization_name?: string;
  organization_status?: string;

  // Scoring & Status
  score: number;
  lifecycle_stage: LifecycleStage;
  status: ContactStatus;

  // Address
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Social
  linkedin_url?: string;
  twitter_handle?: string;

  // Preferences
  preferred_language?: string;
  do_not_call?: boolean;
  do_not_email?: boolean;
  do_not_sms?: boolean;

  // Conversion tracking
  converted_to_user_id?: number;
  converted_at?: string;

  // Tags
  tags?: string[];
  custom_fields?: Record<string, unknown>;

  // Sync & privacy
  source?: string;
  synced_by_user_id?: number;
  visibility?: 'private' | 'shared';
  graph_id?: string;

  // Timestamps
  last_activity?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactListResponse {
  items?: DirectoryContact[];
  contacts?: DirectoryContact[];  // Backend uses 'contacts' field
  total: number;
  page: number;
  page_size: number;
  pages?: number;
  total_pages?: number;  // Backend uses 'total_pages'
}

export interface ContactFilters {
  search?: string;
  cbo_id?: number;
  lifecycle_stage?: LifecycleStage;
  status?: ContactStatus;
  score_min?: number;
  score_max?: number;
  tags?: string[];
  has_organization?: boolean;
  conversion_ready?: boolean;
}

export interface ContactCreateData {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  mobile_phone?: string;
  company_name?: string;
  job_title?: string;
  department?: string;
  cbo_id?: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  linkedin_url?: string;
  twitter_handle?: string;
  preferred_language?: string;
  do_not_call?: boolean;
  do_not_email?: boolean;
  do_not_sms?: boolean;
  tags?: string[];
  custom_fields?: Record<string, unknown>;
}

export interface ImportResult {
  success: boolean;
  total_rows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; email: string; error: string }>;
}

export interface ScoreHistoryEntry {
  id: string;
  contact_id: string;
  previous_score: number;
  new_score: number;
  change_amount: number;
  change_reason: string;
  changed_at: string;
}

export interface ConvertToUserResult {
  success: boolean;
  user_id?: number;
  message: string;
}

export interface Organization {
  id: number;
  name: string;
  short_name?: string;
  customer_status?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  address?: string;
  contact_count?: number;
}

// Contacts API base URL - use direct backend URL like the platform API
const CONTACTS_API_URL = `${PLATFORM_BACKEND_URL}/api/platform/contacts`;

// Helper to get auth headers for contacts API
function getContactsAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const cboId = localStorage.getItem('active_cbo_id');
  if (cboId) {
    headers['X-CBO-ID'] = cboId;
  }
  return headers;
}

export const contactsApi = {
  // List contacts with filters and pagination
  getContacts: async (
    params?: ContactFilters & { page?: number; page_size?: number; sort_by?: string; sort_order?: 'asc' | 'desc' }
  ): Promise<ApiResponse<ContactListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, String(v)));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }
    const url = `${CONTACTS_API_URL}?${searchParams.toString()}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Get single contact
  getContact: async (id: string): Promise<ApiResponse<DirectoryContact>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${id}`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Create contact
  createContact: async (contact: ContactCreateData): Promise<ApiResponse<DirectoryContact>> => {
    try {
      const response = await fetch(CONTACTS_API_URL, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(contact),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Update contact
  updateContact: async (id: string, updates: Partial<ContactCreateData>): Promise<ApiResponse<DirectoryContact>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${id}`, {
        method: 'PUT',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Delete contact
  deleteContact: async (id: string): Promise<ApiResponse<void>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${id}`, {
        method: 'DELETE',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      return { success: response.ok };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Link contact to organization
  linkOrganization: async (contactId: string, cboId: number): Promise<ApiResponse<DirectoryContact>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${contactId}/link-organization`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ cbo_id: cboId }),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Convert contact to user
  convertToUser: async (
    contactId: string,
    options: { role?: string; send_welcome_email?: boolean; notes?: string }
  ): Promise<ApiResponse<ConvertToUserResult>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${contactId}/convert-to-user`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(options),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Bulk tag contacts
  bulkTag: async (contactIds: string[], tags: string[]): Promise<ApiResponse<{ updated_count: number }>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/bulk-tag`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ contact_ids: contactIds, tags }),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Bulk delete contacts
  bulkDelete: async (contactIds: string[]): Promise<ApiResponse<void>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/bulk-delete`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ contact_ids: contactIds }),
      });
      return { success: response.ok };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Import contacts from CSV
  importCSV: async (
    file: File,
    options: { duplicate_handling?: 'skip' | 'update' | 'create_new'; default_tags?: string[] }
  ): Promise<ApiResponse<ImportResult>> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (options.duplicate_handling) {
        formData.append('duplicate_handling', options.duplicate_handling);
      }
      if (options.default_tags) {
        options.default_tags.forEach(tag => formData.append('default_tags', tag));
      }
      // For file uploads, only add Authorization header (not Content-Type)
      const headers: HeadersInit = {};
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const cboId = localStorage.getItem('active_cbo_id');
      if (cboId) {
        headers['X-CBO-ID'] = cboId;
      }
      const response = await fetch(`${CONTACTS_API_URL}/import/csv`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Export contacts
  exportContacts: async (format: 'csv' | 'json' = 'csv', filters?: ContactFilters): Promise<Blob | null> => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('format', format);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
          }
        });
      }
      const response = await fetch(`${CONTACTS_API_URL}/export?${searchParams.toString()}`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        return await response.blob();
      }
      return null;
    } catch (error) {
      console.error('Export failed:', error);
      return null;
    }
  },

  // Get contact score history
  getScoreHistory: async (contactId: string): Promise<ApiResponse<ScoreHistoryEntry[]>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/${contactId}/score-history`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Get contacts by organization
  getOrganizationContacts: async (organizationId: number): Promise<ApiResponse<DirectoryContact[]>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/organizations/${organizationId}/contacts`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Get conversion-ready contacts for an organization
  getConversionCandidates: async (organizationId: number): Promise<ApiResponse<DirectoryContact[]>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/organizations/${organizationId}/conversion-candidates`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Sync contacts from Outlook via Microsoft Graph
  syncOutlookContacts: async (): Promise<ApiResponse<{ synced: number; updated: number; skipped: number; total_fetched: number }>> => {
    try {
      const CRM_API = `${PLATFORM_BACKEND_URL}/api/crm-activities`;
      const response = await fetch(`${CRM_API}/contact-sync/outlook`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: { code: 'SYNC_ERROR', message: data.detail || 'Sync failed' } };
      }
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Share a private contact with the organization
  shareContact: async (contactId: string): Promise<ApiResponse<{ success: boolean }>> => {
    try {
      const CRM_API = `${PLATFORM_BACKEND_URL}/api/crm-activities`;
      const response = await fetch(`${CRM_API}/contacts/${contactId}/share`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Make a shared contact private again
  unshareContact: async (contactId: string): Promise<ApiResponse<{ success: boolean }>> => {
    try {
      const CRM_API = `${PLATFORM_BACKEND_URL}/api/crm-activities`;
      const response = await fetch(`${CRM_API}/contacts/${contactId}/unshare`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },

  // Bulk share multiple contacts
  bulkShareContacts: async (contactIds: string[]): Promise<ApiResponse<{ shared_count: number }>> => {
    try {
      const CRM_API = `${PLATFORM_BACKEND_URL}/api/crm-activities`;
      const response = await fetch(`${CRM_API}/contacts/bulk-share`, {
        method: 'POST',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(contactIds),
      });
      const data = await response.json();
      return { success: response.ok, data };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },
};

// ============================================================================
// ORGANIZATIONS API (uses platform contacts API for CBOs)
// ============================================================================

export const organizationsApi = {
  // List all organizations/CBOs - uses platform backend
  getOrganizations: async (): Promise<ApiResponse<Organization[]>> => {
    try {
      const response = await fetch(`${CONTACTS_API_URL}/organizations`, {
        method: 'GET',
        headers: getContactsAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      // API returns { organizations: [], total: number }
      const orgs = data.organizations || data.data || (Array.isArray(data) ? data : []);
      return { success: response.ok, data: orgs };
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' }
      };
    }
  },
};

// ============================================================================
// VISITOR IDENTIFICATION TYPES
// ============================================================================

export type VisitorStatus = 'anonymous' | 'identified' | 'converted' | 'ignored';
export type CompanyStatus = 'discovered' | 'reviewing' | 'prospect' | 'qualified' | 'customer' | 'ignored';
export type QueueType = 'review' | 'prospect_create' | 'enrich' | 'delete';
export type QueueItemStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface Visitor {
  id: string;
  visitor_fingerprint: string;
  contact_id?: string;
  company_id?: string;
  status: VisitorStatus;
  first_ip_address?: string;
  last_ip_address?: string;
  identified_at?: string;
  identified_by?: string;
  company_confidence?: number;
  total_visits: number;
  total_page_views: number;
  total_time_seconds: number;
  first_seen_at: string;
  last_seen_at: string;
  behavior_score: number;
  intent_score: number;
  created_at: string;
  updated_at: string;
  // Latest visit data (from vi_visits JOIN)
  last_device_type?: string;
  last_browser?: string;
  last_os?: string;
  last_ip?: string;
  last_geo_city?: string;
  last_geo_country?: string;
  // Company name (from vi_companies JOIN)
  company_name?: string;
}

export interface VisitorCompany {
  id: string;
  company_name: string;
  domain?: string;
  cbo_id?: number;
  industry?: string;
  industry_group?: string;
  employee_count_range?: string;
  employee_count?: number;
  annual_revenue_range?: string;
  headquarters_city?: string;
  headquarters_state?: string;
  headquarters_country?: string;
  website_url?: string;
  linkedin_url?: string;
  logo_url?: string;
  description?: string;
  technologies?: string[];
  status: CompanyStatus;
  enrichment_source?: string;
  enrichment_confidence?: number;
  first_seen_at: string;
  last_seen_at: string;
  total_visitors: number;
  total_visits: number;
  total_page_views: number;
  fit_score: number;
  intent_score: number;
  created_at: string;
  updated_at: string;
}

export interface ProspectQueueItem {
  id: string;
  company_id: string;
  queue_type: QueueType;
  status: QueueItemStatus;
  priority: number;
  notes?: string;
  action_data?: Record<string, unknown>;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  company?: VisitorCompany;
}

export interface TrackingSnippet {
  site_id: string;
  snippet_html: string;
  snippet_script_url: string;
  instructions: string;
}

export interface VisitorAnalytics {
  total_visitors: number;
  total_companies: number;
  identified_visitors: number;
  anonymous_visitors: number;
  total_page_views: number;
  total_sessions: number;
  visitors_by_day: Array<{ date: string; count: number }>;
  top_companies: Array<{ company_name: string; visits: number }>;
  top_pages: Array<{ page_url: string; views: number }>;
}

// ============================================================================
// ENRICHMENT TYPES
// ============================================================================

export type EnrichmentProviderType = 'native_registry' | 'cbo_network' | 'form_correlation' | 'partner_intel' | 'reverse_dns' | 'whois' | 'asn' | 'clearbit' | 'zoominfo' | 'ipinfo' | 'maxmind';
export type EnrichmentTier = 'native' | 'native_tools' | 'third_party';

export interface EnrichmentProvider {
  id: string;
  provider_name: EnrichmentProviderType;
  display_name: string;
  tier: EnrichmentTier;
  priority: number;
  is_enabled: boolean;
  api_key_encrypted?: string;
  api_key_set: boolean;
  monthly_request_limit?: number;
  daily_request_limit?: number;
  cost_per_request?: number;
  monthly_budget?: number;
  current_month_requests: number;
  current_month_cost: number;
  today_requests: number;
  confidence_threshold: number;
  average_confidence?: number;
  success_rate?: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EnrichmentResult {
  success: boolean;
  ip_address: string;
  company_name?: string;
  domain?: string;
  industry?: string;
  employee_count_range?: string;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  confidence: number;
  source: string;
  providers_attempted: string[];
  cost: number;
}

export interface EnrichmentAnalytics {
  total_enrichments: number;
  successful_enrichments: number;
  failed_enrichments: number;
  success_rate: number;
  total_cost: number;
  average_confidence: number;
  enrichments_by_provider: Array<{
    provider: string;
    requests: number;
    successes: number;
    failures: number;
    cost: number;
    avg_confidence: number;
  }>;
  enrichments_by_day: Array<{
    date: string;
    total: number;
    successful: number;
    cost: number;
  }>;
}

export interface IPMapping {
  id: string;
  ip_address: string;
  company_id: string;
  company_name?: string;
  source: 'manual' | 'enrichment' | 'form';
  confidence: number;
  notes?: string;
  created_at: string;
  created_by?: string;
}

// ============================================================================
// VISITOR IDENTIFICATION API
// ============================================================================

export const visitorIdentificationApi = {
  // Get tracking snippet for embedding on websites
  getTrackingSnippet: async (siteId?: string): Promise<ApiResponse<TrackingSnippet>> => {
    const api = getPlatformApi();
    const params = siteId ? `?site_id=${siteId}` : '';
    return api.get<TrackingSnippet>(`/visitor-id/tracking-snippet${params}`);
  },

  // List visitors with filtering
  getVisitors: async (params?: {
    status?: VisitorStatus;
    company_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ visitors: Visitor[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.company_id) queryParams.append('company_id', params.company_id);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/visitor-id/visitors${query}`);
  },

  // Get single visitor with timeline
  getVisitor: async (visitorId: string): Promise<ApiResponse<Visitor>> => {
    const api = getPlatformApi();
    return api.get<Visitor>(`/visitor-id/visitors/${visitorId}`);
  },

  // Identify a visitor (link to contact)
  identifyVisitor: async (visitorId: string, contactId: string): Promise<ApiResponse<Visitor>> => {
    const api = getPlatformApi();
    return api.post<Visitor>(`/visitor-id/visitors/${visitorId}/identify`, { contact_id: contactId });
  },

  // Add visitor to prospect queue
  addVisitorToQueue: async (
    visitorId: string,
    queueType: QueueType,
    priority?: number,
    notes?: string
  ): Promise<ApiResponse<ProspectQueueItem>> => {
    const api = getPlatformApi();
    return api.post<ProspectQueueItem>(`/visitor-id/visitors/${visitorId}/queue`, {
      queue_type: queueType,
      priority: priority || 50,
      notes,
    });
  },

  // Link visitor to company (after enrichment review)
  linkVisitorToCompany: async (
    visitorId: string,
    data: {
      company_name: string;
      domain?: string;
      industry?: string;
      from_enrichment?: boolean;
      enrichment_source?: string;
      enrichment_confidence?: number;
    }
  ): Promise<ApiResponse<Visitor>> => {
    const api = getPlatformApi();
    return api.post<Visitor>(`/visitor-id/visitors/${visitorId}/link-company`, data);
  },

  // Skip enrichment review for a visitor
  skipVisitorReview: async (visitorId: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    const api = getPlatformApi();
    return api.post(`/visitor-id/visitors/${visitorId}/skip-review`, {});
  },

  // Get visitor timeline (visits, page views, events)
  getVisitorTimeline: async (visitorId: string, limit?: number): Promise<ApiResponse<{
    visits: Array<{
      id: string;
      session_id: string;
      started_at: string;
      ended_at?: string;
      duration_seconds?: number;
      ip_address: string;
      device_type?: string;
      browser?: string;
      referrer_type?: string;
      utm_source?: string;
      utm_campaign?: string;
      page_views: number;
      landing_page_url?: string;
    }>;
    page_views: Array<{
      id: string;
      page_url: string;
      page_path: string;
      page_title?: string;
      viewed_at: string;
      time_on_page_seconds?: number;
      scroll_depth_percent?: number;
    }>;
    events: Array<{
      id: string;
      event_category: string;
      event_action?: string;
      event_label?: string;
      event_value?: number;
      page_url?: string;
      occurred_at: string;
    }>;
  }>> => {
    const api = getPlatformApi();
    const params = limit ? `?limit=${limit}` : '';
    return api.get(`/visitor-id/visitors/${visitorId}/timeline${params}`);
  },

  // List companies
  getCompanies: async (params?: {
    status?: CompanyStatus;
    min_fit_score?: number;
    min_intent_score?: number;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ companies: VisitorCompany[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.min_fit_score) queryParams.append('min_fit_score', params.min_fit_score.toString());
    if (params?.min_intent_score) queryParams.append('min_intent_score', params.min_intent_score.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/visitor-id/companies${query}`);
  },

  // Get single company
  getCompany: async (companyId: string): Promise<ApiResponse<VisitorCompany>> => {
    const api = getPlatformApi();
    return api.get<VisitorCompany>(`/visitor-id/companies/${companyId}`);
  },

  // Update company status
  updateCompanyStatus: async (companyId: string, status: CompanyStatus): Promise<ApiResponse<VisitorCompany>> => {
    const api = getPlatformApi();
    return api.put<VisitorCompany>(`/visitor-id/companies/${companyId}`, { status });
  },

  // Update company details (name, domain, industry, etc.)
  updateCompany: async (companyId: string, data: {
    company_name?: string;
    domain?: string;
    industry?: string;
    employee_count_range?: string;
    cbo_id?: number;
    status?: CompanyStatus;
  }): Promise<ApiResponse<VisitorCompany>> => {
    const api = getPlatformApi();
    return api.put<VisitorCompany>(`/visitor-id/companies/${companyId}`, data);
  },

  // Enrich company with additional data
  enrichCompany: async (companyId: string): Promise<ApiResponse<VisitorCompany>> => {
    const api = getPlatformApi();
    return api.put<VisitorCompany>(`/visitor-id/companies/${companyId}/enrich`, {});
  },

  // Get visitors for a company
  getCompanyVisitors: async (companyId: string): Promise<ApiResponse<Visitor[]>> => {
    const api = getPlatformApi();
    return api.get<Visitor[]>(`/visitor-id/companies/${companyId}/visitors`);
  },

  // Get prospect queue
  getProspectQueue: async (params?: {
    queue_type?: QueueType;
    status?: QueueItemStatus;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ items: ProspectQueueItem[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.queue_type) queryParams.append('queue_type', params.queue_type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/visitor-id/prospect-queue${query}`);
  },

  // Add company to prospect queue
  addToProspectQueue: async (companyId: string, queueType: QueueType, notes?: string): Promise<ApiResponse<ProspectQueueItem>> => {
    const api = getPlatformApi();
    return api.post<ProspectQueueItem>('/visitor-id/prospect-queue', {
      company_id: companyId,
      queue_type: queueType,
      notes,
    });
  },

  // Process queue item (create prospect, ignore, delete)
  processQueueItem: async (queueId: string, action: 'complete' | 'skip', actionData?: Record<string, unknown>): Promise<ApiResponse<ProspectQueueItem>> => {
    const api = getPlatformApi();
    return api.put<ProspectQueueItem>(`/visitor-id/prospect-queue/${queueId}`, {
      status: action === 'complete' ? 'completed' : 'skipped',
      action_data: actionData,
    });
  },

  // Get visitor analytics
  getAnalytics: async (params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<VisitorAnalytics>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<VisitorAnalytics>(`/visitor-id/analytics${query}`);
  },

  // ============================================================================
  // ENRICHMENT PROVIDERS
  // ============================================================================

  // Get enrichment providers
  getEnrichmentProviders: async (): Promise<ApiResponse<EnrichmentProvider[]>> => {
    const api = getPlatformApi();
    return api.get<EnrichmentProvider[]>('/visitor-id/enrichment/providers');
  },

  // Update enrichment provider
  updateEnrichmentProvider: async (providerName: string, data: Partial<EnrichmentProvider>): Promise<ApiResponse<EnrichmentProvider>> => {
    const api = getPlatformApi();
    return api.put<EnrichmentProvider>(`/visitor-id/enrichment/providers/${providerName}`, data);
  },

  // Manual IP enrichment lookup
  lookupIP: async (ipAddress: string): Promise<ApiResponse<EnrichmentResult>> => {
    const api = getPlatformApi();
    // Backend returns EnrichmentLookupResponse, map to EnrichmentResult
    const response = await api.post<{
      ip_address: string;
      company_name?: string;
      company_domain?: string;
      company_id?: string;
      confidence?: number;
      source?: string;
      providers_attempted: string[];
      duration_ms: number;
      third_party_cost: number;
    }>('/visitor-id/enrichment/lookup', { ip_address: ipAddress });

    if (response.success && response.data) {
      // Map backend response to frontend EnrichmentResult
      const mapped: EnrichmentResult = {
        success: !!response.data.company_name,
        ip_address: response.data.ip_address,
        company_name: response.data.company_name,
        domain: response.data.company_domain,
        confidence: response.data.confidence || 0,
        source: response.data.source || '',
        providers_attempted: response.data.providers_attempted,
        cost: response.data.third_party_cost,
      };
      return { success: true, data: mapped };
    }
    return { success: false, error: response.error };
  },

  // Get enrichment analytics
  getEnrichmentAnalytics: async (): Promise<ApiResponse<EnrichmentAnalytics>> => {
    const api = getPlatformApi();
    return api.get<EnrichmentAnalytics>('/visitor-id/enrichment/analytics');
  },

  // Get IP mappings
  getIPMappings: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ mappings: IPMapping[]; total: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/visitor-id/ip-mappings${query}`);
  },

  // Create manual IP mapping
  createIPMapping: async (data: { ip_address: string; company_id: string; notes?: string }): Promise<ApiResponse<IPMapping>> => {
    const api = getPlatformApi();
    return api.post<IPMapping>('/visitor-id/ip-mappings', data);
  },

  // Delete IP mapping
  deleteIPMapping: async (mappingId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/visitor-id/ip-mappings/${mappingId}`);
  },

  // Run batch enrichment for unenriched visitors
  runBatchEnrichment: async (limit: number = 100): Promise<ApiResponse<{ queued_count: number; message: string }>> => {
    const api = getPlatformApi();
    return api.post<{ queued_count: number; message: string }>(`/visitor-id/enrichment/batch?limit=${limit}`, {});
  },

  // Auto-create prospects from enriched companies
  autoCreateProspects: async (params?: {
    min_confidence?: number;
    min_visits?: number;
  }): Promise<ApiResponse<{ created_count: number; candidates_found: number; message: string }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.min_confidence !== undefined) queryParams.append('min_confidence', params.min_confidence.toString());
    if (params?.min_visits !== undefined) queryParams.append('min_visits', params.min_visits.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.post<{ created_count: number; candidates_found: number; message: string }>(`/visitor-id/enrichment/auto-prospect${query}`, {});
  },
};

// ============================================================================
// COLD OUTREACH TYPES
// ============================================================================

export type CampaignStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'cancelled';
export type CampaignType = 'cold_email' | 'linkedin' | 'multi_channel';
export type StepType = 'email' | 'linkedin_connect' | 'linkedin_message' | 'call' | 'delay' | 'condition';
export type ProspectStatus = 'pending' | 'active' | 'paused' | 'completed' | 'replied' | 'bounced' | 'unsubscribed' | 'opted_out';
export type ActivityType = 'email_sent' | 'email_opened' | 'email_clicked' | 'email_bounced' | 'email_replied' | 'linkedin_sent' | 'linkedin_accepted' | 'linkedin_replied' | 'call_made' | 'meeting_booked' | 'opted_out' | 'unsubscribed';
export type ReplyClassification = 'positive' | 'negative' | 'neutral' | 'out_of_office' | 'bounce' | 'unsubscribe' | 'meeting_request' | 'referral' | 'not_interested' | 'wrong_person';

export interface ColdOutreachCampaign {
  id: string;
  name: string;
  description?: string;
  campaign_type: CampaignType;
  status: CampaignStatus;
  // TRUS-772 personalization
  personalization_mode?: 'off' | 'review' | 'auto' | null;
  booking_url?: string | null;
  offer_summary?: string | null;
  personalization_model?: string | null;
  from_email?: string;
  from_name?: string;
  reply_to_email?: string;
  daily_send_limit: number;
  timezone: string;
  sending_days: string[];
  sending_start_time: string;
  sending_end_time: string;
  min_delay_between_emails: number;
  track_opens: boolean;
  track_clicks: boolean;
  stop_on_reply: boolean;
  stop_on_meeting: boolean;
  total_prospects: number;
  prospects_contacted: number;
  prospects_opened: number;
  prospects_clicked: number;
  prospects_replied: number;
  prospects_bounced: number;
  meetings_booked: number;
  scheduled_start_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SequenceStep {
  id: string;
  campaign_id: string;
  step_number: number;
  step_type: StepType;
  name?: string;
  delay_days: number;
  delay_hours: number;
  subject_template?: string;
  body_template?: string;
  is_active: boolean;
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_replied: number;
  created_at: string;
  updated_at: string;
}

export interface ProspectBriefObservation {
  fact: string;
  source_url: string;
}

export interface ProspectBriefFocusArea {
  title: string;
  why_it_matters: string;
  how_we_help: string;
  source_url: string;
}

export interface ProspectBrief {
  prospect_id: string;
  campaign_id: string;
  status: 'pending' | 'researching' | 'drafted' | 'approved' | 'failed';
  research: {
    summary?: string;
    observations?: ProspectBriefObservation[];
    focus_areas?: ProspectBriefFocusArea[];
    angle?: string;
    sources?: Record<string, string>;
  };
  landing: { headline?: string; intro?: string; next_steps_intro?: string };
  draft_subject?: string | null;
  draft_body?: string | null;
  followup_subject?: string | null;
  followup_body?: string | null;
  followup_status?: 'drafted' | 'sent' | null;
  followup_reason?: string | null;
  landing_url?: string | null;
  landing_views: number;
  first_viewed_at?: string | null;
  last_viewed_at?: string | null;
  landing_response?: 'book' | 'question' | 'not_now' | null;
  landing_responded_at?: string | null;
  model?: string | null;
  cost_usd?: number;
  error?: string | null;
  approved_at?: string | null;
  updated_at?: string | null;
}

export interface PersonalizationModelOption {
  id: string;
  label: string;
  tier: string;
  input_per_mtok: number;
  output_per_mtok: number;
  est_cost_per_prospect: number;
  estimate_from_briefs: number;
  server_fallback: boolean;
}

export interface PersonalizationModels {
  default_model: string;
  source: 'env' | 'built_in';
  env_value: string | null;
  env_valid: boolean | null;
  models: PersonalizationModelOption[];
}

export interface ProspectBriefSummary {
  prospect_id: string;
  status: ProspectBrief['status'];
  followup_status?: string | null;
  landing_views: number;
  landing_response?: string | null;
  approved_at?: string | null;
  error?: string | null;
}

export interface ColdOutreachProspect {
  id: string;
  campaign_id: string;
  contact_id?: string;
  discovered_contact_id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  job_title?: string;
  linkedin_url?: string;
  status: ProspectStatus;
  current_step: number;
  last_contacted_at?: string;
  next_step_at?: string;
  total_emails_sent: number;
  total_opens: number;
  total_clicks: number;
  replied_at?: string;
  opted_out_at?: string;
  personalization_data?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ColdOutreachActivity {
  id: string;
  campaign_id: string;
  prospect_id: string;
  step_id?: string;
  activity_type: ActivityType;
  channel: string;
  subject?: string;
  message_preview?: string;
  metadata?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface ColdOutreachReply {
  id: string;
  campaign_id: string;
  prospect_id: string;
  email_message_id?: string;
  from_email: string;
  subject?: string;
  body_preview: string;
  body_html?: string;
  classification?: ReplyClassification;
  sentiment_score?: number;
  action_taken?: string;
  action_notes?: string;
  processed_at?: string;
  received_at: string;
  created_at: string;
}

export interface CampaignAnalytics {
  total_prospects: number;
  contacted: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  meetings_booked: number;
  open_rate: number;
  click_rate: number;
  reply_rate: number;
  bounce_rate: number;
  daily_stats: Array<{
    date: string;
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
  }>;
  step_performance: Array<{
    step_number: number;
    step_type: StepType;
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
  }>;
}

// ============================================================================
// COLD OUTREACH API
// ============================================================================

export interface EmailTemplate {
  id: string;
  template_name: string;
  template_type: string;
  category: string | null;
  description: string | null;
  subject_line: string | null;
  body_content: string;
  personalization_tokens: string[];
  is_active: boolean;
  is_custom: boolean;
  avg_open_rate: number | null;
  avg_reply_rate: number | null;
  created_at: string;
  updated_at: string;
}

export interface WarmupStatusSummary {
  domain: string;
  warmup_status: string;
  warmup_stage: number;
  warmup_start_date: string | null;
  daily_limit: number;
  sent_today: number;
  remaining_today: number;
  last_send_at: string | null;
  health_score: number;
  sendgrid_authenticated: boolean;
  active_mailboxes: number;
  seed_inboxes_configured: number;
  send_interval_seconds: number;
  blockers: string[];
  is_progressing: boolean;
}

export interface WarmupSendRecord {
  id: string;
  mailbox_id: string | null;
  from_email: string | null;
  to_email: string;
  subject: string | null;
  status: 'sent' | 'failed';
  error: string | null;
  triggered_by: string;
  sent_at: string;
}

export interface SenderDomain {
  domain_id: string;
  domain: string;
  warmup_status: string;
  is_active_for_sending: boolean;
  sendgrid_authenticated: boolean;
  health_score: number;
  daily_limit: number;
  needs_mailbox: boolean;
  mailboxes: Array<{ mailbox_id: string; email_address: string; display_name: string | null }>;
}

export interface SendersResponse {
  domains: SenderDomain[];
  recommended_sender: {
    domain: string;
    domain_id: string;
    email_address: string;
    display_name: string | null;
    warmup_status: string;
  } | null;
}

export const coldOutreachApi = {
  // List campaigns
  getCampaigns: async (params?: {
    status?: CampaignStatus;
    campaign_type?: CampaignType;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ campaigns: ColdOutreachCampaign[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.campaign_type) queryParams.append('campaign_type', params.campaign_type);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/cold-outreach/campaigns${query}`);
  },

  // Get single campaign
  getCampaign: async (campaignId: string): Promise<ApiResponse<ColdOutreachCampaign>> => {
    const api = getPlatformApi();
    return api.get<ColdOutreachCampaign>(`/cold-outreach/campaigns/${campaignId}`);
  },

  // Create campaign
  createCampaign: async (
    data: Partial<ColdOutreachCampaign> & {
      source_list_id?: string;
      source_list_type?: 'lead_builder' | 'contact_list';
      template_id?: string;
    },
  ): Promise<ApiResponse<ColdOutreachCampaign>> => {
    const api = getPlatformApi();
    return api.post<ColdOutreachCampaign>('/cold-outreach/campaigns', data);
  },

  // Update campaign
  updateCampaign: async (campaignId: string, data: Partial<ColdOutreachCampaign>): Promise<ApiResponse<ColdOutreachCampaign>> => {
    const api = getPlatformApi();
    return api.put<ColdOutreachCampaign>(`/cold-outreach/campaigns/${campaignId}`, data);
  },

  // Delete campaign
  deleteCampaign: async (campaignId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/campaigns/${campaignId}`);
  },

  // Start campaign
  startCampaign: async (campaignId: string): Promise<ApiResponse<ColdOutreachCampaign>> => {
    const api = getPlatformApi();
    return api.post<ColdOutreachCampaign>(`/cold-outreach/campaigns/${campaignId}/start`, {});
  },

  // Pause campaign
  pauseCampaign: async (campaignId: string): Promise<ApiResponse<ColdOutreachCampaign>> => {
    const api = getPlatformApi();
    return api.post<ColdOutreachCampaign>(`/cold-outreach/campaigns/${campaignId}/pause`, {});
  },

  // Get campaign analytics
  getCampaignAnalytics: async (campaignId: string): Promise<ApiResponse<CampaignAnalytics>> => {
    const api = getPlatformApi();
    return api.get<CampaignAnalytics>(`/cold-outreach/campaigns/${campaignId}/analytics`);
  },

  // ============================================================================
  // SEQUENCE STEPS
  // ============================================================================

  // Get campaign steps
  getSteps: async (campaignId: string): Promise<ApiResponse<SequenceStep[]>> => {
    const api = getPlatformApi();
    return api.get<SequenceStep[]>(`/cold-outreach/campaigns/${campaignId}/steps`);
  },

  // Create step
  createStep: async (campaignId: string, data: Partial<SequenceStep>): Promise<ApiResponse<SequenceStep>> => {
    const api = getPlatformApi();
    return api.post<SequenceStep>(`/cold-outreach/campaigns/${campaignId}/steps`, data);
  },

  // Update step
  updateStep: async (campaignId: string, stepId: string, data: Partial<SequenceStep>): Promise<ApiResponse<SequenceStep>> => {
    const api = getPlatformApi();
    return api.put<SequenceStep>(`/cold-outreach/campaigns/${campaignId}/steps/${stepId}`, data);
  },

  // Delete step
  deleteStep: async (campaignId: string, stepId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/campaigns/${campaignId}/steps/${stepId}`);
  },

  // Reorder steps
  reorderSteps: async (campaignId: string, stepIds: string[]): Promise<ApiResponse<SequenceStep[]>> => {
    const api = getPlatformApi();
    return api.post<SequenceStep[]>(`/cold-outreach/campaigns/${campaignId}/steps/reorder`, { step_ids: stepIds });
  },

  // ============================================================================
  // PROSPECTS
  // ============================================================================

  // Get campaign prospects
  getProspects: async (campaignId: string, params?: {
    status?: ProspectStatus;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ prospects: ColdOutreachProspect[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/cold-outreach/campaigns/${campaignId}/prospects${query}`);
  },

  // Add prospect to campaign
  addProspect: async (campaignId: string, data: Partial<ColdOutreachProspect>): Promise<ApiResponse<ColdOutreachProspect>> => {
    const api = getPlatformApi();
    return api.post<ColdOutreachProspect>(`/cold-outreach/campaigns/${campaignId}/prospects`, data);
  },

  // Bulk add prospects
  bulkAddProspects: async (campaignId: string, prospects: Array<Partial<ColdOutreachProspect>>): Promise<ApiResponse<{ added: number; skipped: number; errors: string[] }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/bulk`, { prospects });
  },

  // Update prospect
  updateProspect: async (campaignId: string, prospectId: string, data: Partial<ColdOutreachProspect>): Promise<ApiResponse<ColdOutreachProspect>> => {
    const api = getPlatformApi();
    return api.put<ColdOutreachProspect>(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}`, data);
  },

  // Remove prospect from campaign
  removeProspect: async (campaignId: string, prospectId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}`);
  },

  // ============================================================================
  // ACTIVITIES & REPLIES
  // ============================================================================

  // Get campaign activities
  getActivities: async (campaignId: string, params?: {
    prospect_id?: string;
    activity_type?: ActivityType;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ activities: ColdOutreachActivity[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.prospect_id) queryParams.append('prospect_id', params.prospect_id);
    if (params?.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/cold-outreach/campaigns/${campaignId}/activities${query}`);
  },

  // Get campaign replies
  getReplies: async (campaignId: string, params?: {
    classification?: ReplyClassification;
    unprocessed?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ replies: ColdOutreachReply[]; total: number; page: number; page_size: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.classification) queryParams.append('classification', params.classification);
    if (params?.unprocessed) queryParams.append('unprocessed', 'true');
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/cold-outreach/campaigns/${campaignId}/replies${query}`);
  },

  // Classify reply
  classifyReply: async (campaignId: string, replyId: string, classification: ReplyClassification, actionTaken?: string, actionNotes?: string): Promise<ApiResponse<ColdOutreachReply>> => {
    const api = getPlatformApi();
    return api.put<ColdOutreachReply>(`/cold-outreach/campaigns/${campaignId}/replies/${replyId}`, {
      classification,
      action_taken: actionTaken,
      action_notes: actionNotes,
    });
  },

  // ============================================================================
  // TEMPLATES
  // ============================================================================

  // Get email templates (shared library + tenant custom)
  getTemplates: async (templateType?: string): Promise<ApiResponse<{ templates: EmailTemplate[] }>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/templates${templateType ? `?template_type=${templateType}` : ''}`);
  },

  createTemplate: async (data: {
    template_name: string;
    template_type?: string;
    category?: string;
    description?: string;
    subject_line?: string;
    body_content: string;
  }): Promise<ApiResponse<EmailTemplate>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/templates', data);
  },

  updateTemplate: async (templateId: string, data: Partial<{
    template_name: string;
    template_type: string;
    category: string;
    description: string;
    subject_line: string;
    body_content: string;
    is_active: boolean;
  }>): Promise<ApiResponse<EmailTemplate>> => {
    const api = getPlatformApi();
    return api.put(`/cold-outreach/templates/${templateId}`, data);
  },

  deleteTemplate: async (templateId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/templates/${templateId}`);
  },

  // Send a single engagement email to one prospect right now
  // ---- Personalization (TRUS-772) ----
  getPersonalizationModels: async (): Promise<ApiResponse<PersonalizationModels>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/personalization/models`);
  },

  getBriefs: async (campaignId: string): Promise<ApiResponse<{ briefs: ProspectBriefSummary[]; total_cost_usd?: number }>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/campaigns/${campaignId}/briefs`);
  },

  getBrief: async (campaignId: string, prospectId: string): Promise<ApiResponse<{ brief: ProspectBrief | null }>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/brief`);
  },

  generateBrief: async (campaignId: string, prospectId: string): Promise<ApiResponse<{ brief: ProspectBrief }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/brief`, {});
  },

  updateBrief: async (
    campaignId: string,
    prospectId: string,
    data: { draft_subject?: string; draft_body?: string; followup_subject?: string; followup_body?: string },
  ): Promise<ApiResponse<{ brief: ProspectBrief }>> => {
    const api = getPlatformApi();
    return api.patch(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/brief`, data);
  },

  approveBrief: async (campaignId: string, prospectId: string): Promise<ApiResponse<{ brief: ProspectBrief }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/brief/approve`, {});
  },

  personalizeCampaign: async (campaignId: string): Promise<ApiResponse<{ queued: number }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/personalize`, {});
  },

  sendFollowup: async (
    campaignId: string,
    prospectId: string,
  ): Promise<ApiResponse<{ status: string; to_email: string; subject: string }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/brief/followup/send`, {});
  },

  sendProspectNow: async (
    campaignId: string,
    prospectId: string,
    data?: { subject?: string; body?: string; step_id?: string; template_id?: string; from_email?: string; from_name?: string },
  ): Promise<ApiResponse<{ status: string; to_email: string; from_email: string; subject: string }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/${prospectId}/send-now`, data || {});
  },

  // ============================================================================
  // DOMAIN POOL
  // ============================================================================

  getDomains: async (params?: { warmup_status?: string; is_active?: boolean; page?: number; page_size?: number }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    const qp = new URLSearchParams();
    if (params?.warmup_status) qp.append('warmup_status', params.warmup_status);
    if (params?.is_active !== undefined) qp.append('is_active', String(params.is_active));
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.page_size) qp.append('page_size', params.page_size.toString());
    const q = qp.toString() ? `?${qp.toString()}` : '';
    return api.get(`/cold-outreach/domains/${q}`);
  },

  getDomain: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}`);
  },

  generateDomainSuggestions: async (primaryDomain: string, limit?: number): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/generate-suggestions', { primary_domain: primaryDomain, limit: limit || 20 });
  },

  checkDomainAvailability: async (domains: string[], registrar: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/check-availability', { domains, registrar });
  },

  purchaseDomain: async (data: { domain: string; primary_domain: string; registrar: string; years?: number }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/purchase', data);
  },

  addExternalDomain: async (domain: string, primaryDomain: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/', { domain, primary_domain: primaryDomain });
  },

  deleteDomain: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/domains/${domainId}`);
  },

  configureDns: async (domainId: string, emailProvider?: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/configure-dns`, { email_provider: emailProvider || 'sendgrid' });
  },

  verifyDns: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/verify-dns`, {});
  },

  getDnsRecords: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}/dns-records`);
  },

  authenticateSendgrid: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/sendgrid-auth`, {});
  },

  validateSendgrid: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/sendgrid-validate`, {});
  },

  createMailbox: async (domainId: string, data: { email_address: string; display_name: string; provider?: string }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/create-mailbox`, data);
  },

  getMailboxes: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}/mailboxes`);
  },

  startWarmup: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/warmup/start`, {});
  },

  pauseWarmup: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/warmup/pause`, {});
  },

  getWarmupLog: async (domainId: string, days?: number): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}/warmup/log${days ? `?days=${days}` : ''}`);
  },

  triggerWarmupSends: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/warmup/send-now`, {});
  },

  // Consolidated warmup progress: sent today vs limit, last send, blockers
  getWarmupStatus: async (domainId: string): Promise<ApiResponse<WarmupStatusSummary>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}/warmup/status`);
  },

  // Recent individual warmup sends (newest first)
  getWarmupSends: async (domainId: string, limit?: number): Promise<ApiResponse<{ sends: WarmupSendRecord[] }>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/${domainId}/warmup/sends${limit ? `?limit=${limit}` : ''}`);
  },

  // Flattened sender identities (domains + mailboxes) for campaign creation
  getSenders: async (): Promise<ApiResponse<SendersResponse>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/domains/senders');
  },

  getWarmupSeedConfig: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/domains/warmup/seed-config');
  },

  saveWarmupSeedConfig: async (seedEmails: string[]): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/warmup/seed-config', { seed_emails: seedEmails });
  },

  getAgentStatus: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/domains/agent/status');
  },

  triggerAgentRun: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/agent/run-now', {});
  },

  resetDomainAgent: async (domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/${domainId}/agent/reset`, {});
  },

  getDomainHealthOverview: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/domains/health-overview');
  },

  assignDomainsToCampaign: async (campaignId: string, domainPoolIds: string[], weights?: number[]): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/domains/campaigns/${campaignId}/pool`, { domain_pool_ids: domainPoolIds, weights });
  },

  getCampaignDomainPool: async (campaignId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/domains/campaigns/${campaignId}/pool`);
  },

  removeDomainFromCampaign: async (campaignId: string, domainId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/domains/campaigns/${campaignId}/pool/${domainId}`);
  },

  getRegistrars: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/domains/registrars');
  },

  saveRegistrar: async (registrar: string, apiCredentials: Record<string, string>): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/domains/registrars', { registrar, api_credentials: apiCredentials });
  },

  // ============================================================================
  // CALL TASKS
  // ============================================================================

  getCallTasks: async (campaignId: string, params?: { status?: string; assigned_to?: string; page?: number; page_size?: number }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    const qp = new URLSearchParams();
    if (params?.status) qp.append('status', params.status);
    if (params?.assigned_to) qp.append('assigned_to', params.assigned_to);
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.page_size) qp.append('page_size', params.page_size.toString());
    const q = qp.toString() ? `?${qp.toString()}` : '';
    return api.get(`/cold-outreach/campaigns/${campaignId}/call-tasks${q}`);
  },

  getMyCallQueue: async (campaignId?: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    const q = campaignId ? `?campaign_id=${campaignId}` : '';
    return api.get(`/cold-outreach/call-tasks/my-queue${q}`);
  },

  createCallTask: async (campaignId: string, data: any): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/call-tasks`, data);
  },

  initiateCall: async (taskId: string, mode: 'browser' | 'bridge', repPhone?: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/call-tasks/${taskId}/initiate`, { mode, rep_phone: repPhone });
  },

  recordDisposition: async (taskId: string, data: { disposition: string; notes?: string; sequence_action?: string; callback_at?: string }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/call-tasks/${taskId}/disposition`, data);
  },

  rescheduleCall: async (taskId: string, scheduledFor: string, notes?: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/call-tasks/${taskId}/reschedule`, { scheduled_for: scheduledFor, notes });
  },

  skipCall: async (taskId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/call-tasks/${taskId}/skip`, {});
  },

  // Build a call queue on-demand from a campaign's prospects (Phase 2a)
  buildCallQueue: async (
    campaignId: string,
    body: { assign_to_me?: boolean; call_script?: string; limit?: number } = {},
  ): Promise<ApiResponse<{ campaign_id: string; call_tasks_created: number; assigned_to: string | null }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/call-tasks/build`, body);
  },

  // Assign / claim / unassign a call task
  assignCallTask: async (
    taskId: string,
    body: { assign_to_me?: boolean; assigned_to?: string | null } = {},
  ): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.patch(`/cold-outreach/call-tasks/${taskId}/assign`, body);
  },

  getCallTranscription: async (taskId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/cold-outreach/call-tasks/${taskId}/transcription`);
  },

  startPowerDialer: async (campaignId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/power-dialer/start`, {});
  },

  advancePowerDialer: async (campaignId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/power-dialer/next`, {});
  },

  // ============================================================================
  // OUTREACH PHONE NUMBERS
  // ============================================================================

  searchPhoneNumbers: async (params: { country?: string; area_code?: string; number_type?: string; limit?: number }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/phone-numbers/search', params);
  },

  provisionPhoneNumber: async (phoneNumber: string, campaignId?: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/cold-outreach/phone-numbers/provision', { phone_number: phoneNumber, campaign_id: campaignId });
  },

  getPhoneNumbers: async (): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get('/cold-outreach/phone-numbers');
  },

  releasePhoneNumber: async (numberId: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.delete(`/cold-outreach/phone-numbers/${numberId}`);
  },
};

// ============================================================================
// LEAD BUILDER TYPES
// ============================================================================

export interface ICPProfile {
  id: string;
  name: string;
  description?: string;
  target_industries: string[];
  excluded_industries: string[];
  employee_range_min: number;
  employee_range_max: number;
  revenue_min?: number;
  revenue_max?: number;
  insurance_statuses: string[];
  hourly_workforce_pct_min: number;
  frontline_pct_min: number;
  turnover_rate_min: number;
  absenteeism_rate_min?: number;
  eap_utilization_max?: number;
  target_titles: string[];
  target_seniority_levels: string[];
  target_departments: string[];
  target_states: string[];
  target_metros: string[];
  excluded_companies: string[];
  weight_industry: number;
  weight_size: number;
  weight_insurance: number;
  weight_workforce: number;
  weight_pain_points: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;

  // Provider-specific fields
  profile_type?: 'employer' | 'provider';
  target_organization_types?: string[];
  target_services?: string[];
  target_data_sources?: string[];
  min_quality_score?: number;
  provider_accepts_medicaid?: boolean;
  provider_accepts_medicare?: boolean;
  target_languages?: string[];
  search_radius_miles?: number;
  search_center_lat?: number;
  search_center_lon?: number;
  weight_org_type?: number;
  weight_services?: number;
  weight_geography?: number;
  weight_quality?: number;
}

export interface CompanyMatch {
  id: string;
  icp_profile_id: string;
  company_name: string;
  domain?: string;
  linkedin_url?: string;
  industry?: string;
  sub_industry?: string;
  employee_count?: number;
  annual_revenue?: number;
  headquarters_state?: string;
  headquarters_city?: string;
  insurance_status?: string;
  is_self_insured?: boolean;
  hourly_workforce_pct?: number;
  frontline_pct?: number;
  turnover_rate?: number;
  absenteeism_rate?: number;
  eap_utilization?: number;
  score: number;
  tier: number;
  confidence: number;
  match_reasons: string[];
  disqualification_reasons: string[];
  status: 'matched' | 'qualified' | 'disqualified' | 'contacted' | 'converted';
  data_sources: string[];
  enrichment_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;

  // Enrichment / outreach-readiness (derived server-side)
  has_domain?: boolean;
  contacts_count?: number;
  emailable_contacts_count?: number;
  decision_maker_count?: number;
  enrichment_stage?: 'matched' | 'enriched' | 'contacts_discovered' | 'outreach_ready';

  // Provider directory fields
  source_type?: 'discovery' | 'provider_import';
  provider_id?: string;
  organization_type?: string;
  provider_services?: string[];
  accepts_medicaid?: boolean;
  accepts_medicare?: boolean;
  quality_score?: number;
  provider_email?: string;
  provider_phone?: string;
  distance_miles?: number;
}

export interface LeadBuilderJob {
  id: string;
  icp_profile_id: string;
  job_type: 'discovery' | 'enrichment' | 'scoring';
  name?: string;
  description?: string;
  status: 'pending' | 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  config: Record<string, unknown>;
  data_source_tiers: string[];
  max_companies: number;
  max_contacts_per_company: number;
  companies_found: number;
  companies_matched: number;
  contacts_discovered: number;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface IntentSignal {
  id: string;
  company_match_id: string;
  signal_type: string;
  signal_category: 'first_party' | 'behavioral' | 'external';
  signal_strength: number;
  description?: string;
  source?: string;
  source_url?: string;
  raw_data: Record<string, unknown>;
  detected_at: string;
  expires_at?: string;
  created_at: string;
}

export interface DiscoveredContact {
  id: string;
  company_match_id: string;
  job_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  title?: string;
  seniority_level?: string;
  department?: string;
  is_decision_maker: boolean;
  buyer_persona?: string;
  confidence_score: number;
  status: 'discovered' | 'verified' | 'added_to_list' | 'contacted' | 'invalid' | 'duplicate';
  data_source?: string;
  data_source_tier?: string;
  raw_data: Record<string, unknown>;
  discovered_at: string;
  created_at: string;
  updated_at: string;
  // TRUS-770: where the person came from and whether the address can be sent to
  email_status?: 'published' | 'verified' | 'risky' | 'guess' | 'invalid' | null;
  role_class?: string | null;
  evidence?: string | null;
  evidence_url?: string | null;
  source_as_of?: string | null;
  review_status?: 'needs_review' | 'approved' | 'rejected' | 'not_applicable' | null;
  confirmed_on_site?: boolean;
  possibly_stale?: boolean;
  sources?: string[];
}

export interface DecisionMakerRow {
  id: string;
  company_match_id: string | null;
  company_name: string | null;
  organization_type?: string | null;
  headquarters_city?: string | null;
  headquarters_state?: string | null;
  first_name?: string;
  last_name?: string;
  title?: string | null;
  role_class?: string | null;
  is_decision_maker: boolean;
  email?: string | null;
  email_status?: DiscoveredContact['email_status'];
  phone?: string | null;
  data_source?: string;
  evidence?: string | null;
  evidence_url?: string | null;
  source_as_of?: string | null;
  review_status?: string | null;
  confidence_score?: number | null;
  confirmed_on_site: boolean;
  possibly_stale: boolean;
}

export interface DecisionMakersPage {
  items: DecisionMakerRow[];
  total: number;
  page: number;
  page_size: number;
  by_source: { irs_990: number; nppes: number; website: number; paid: number };
  coverage: { provider_orgs: number; searched: number; with_leader: number; with_sendable: number };
}

export interface ContactReviewItem {
  id: string;
  company_match_id: string | null;
  company_name: string | null;
  first_name?: string;
  last_name?: string;
  title?: string;
  role_class?: string | null;
  email?: string | null;
  email_status?: DiscoveredContact['email_status'];
  phone?: string | null;
  data_source?: string;
  evidence?: string | null;
  evidence_url?: string | null;
  source_as_of?: string | null;
  confidence_score?: number | null;
  possibly_stale: boolean;
  confirmed_on_site: boolean;
  sources: string[];
}

export interface LeadBuilderAnalytics {
  total_icp_profiles: number;
  active_profiles: number;
  total_companies_matched: number;
  companies_by_tier: Record<number, number>;
  total_jobs: number;
  jobs_by_status: Record<string, number>;
  total_contacts_discovered: number;
  contacts_by_status: Record<string, number>;
}

// ============================================================================
// LEAD BUILDER API
// ============================================================================

export const leadBuilderApi = {
  // ============================================================================
  // ICP PROFILES
  // ============================================================================

  // List ICP profiles
  getICPProfiles: async (params?: { is_active?: boolean }): Promise<ApiResponse<ICPProfile[]>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/lead-builder/icp-profiles${query}`);
  },

  // Get single ICP profile
  getICPProfile: async (profileId: string): Promise<ApiResponse<ICPProfile>> => {
    const api = getPlatformApi();
    return api.get<ICPProfile>(`/lead-builder/icp-profiles/${profileId}`);
  },

  // Create ICP profile
  createICPProfile: async (data: Partial<ICPProfile>): Promise<ApiResponse<ICPProfile>> => {
    const api = getPlatformApi();
    return api.post<ICPProfile>('/lead-builder/icp-profiles', data);
  },

  // Update ICP profile
  updateICPProfile: async (profileId: string, data: Partial<ICPProfile>): Promise<ApiResponse<ICPProfile>> => {
    const api = getPlatformApi();
    return api.put<ICPProfile>(`/lead-builder/icp-profiles/${profileId}`, data);
  },

  // Delete ICP profile
  deleteICPProfile: async (profileId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/lead-builder/icp-profiles/${profileId}`);
  },

  // Duplicate ICP profile
  duplicateICPProfile: async (profileId: string): Promise<ApiResponse<ICPProfile>> => {
    const api = getPlatformApi();
    return api.post<ICPProfile>(`/lead-builder/icp-profiles/${profileId}/duplicate`, {});
  },

  // ============================================================================
  // COMPANY MATCHES
  // ============================================================================

  // List company matches with filters and pagination
  getCompanyMatches: async (params?: {
    icp_profile_id?: string;
    status?: string;
    tier?: number;
    min_score?: number;
    min_employees?: number;
    max_employees?: number;
    state?: string;
    is_self_insured?: boolean;
    plan_type?: string;
    search?: string;
    stage?: string;
    sort_by?: string;
    sort_dir?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ results: CompanyMatch[]; total_count: number; limit: number; offset: number }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.icp_profile_id) queryParams.append('icp_profile_id', params.icp_profile_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.tier !== undefined) queryParams.append('tier', params.tier.toString());
    if (params?.min_score !== undefined) queryParams.append('min_score', params.min_score.toString());
    if (params?.min_employees !== undefined) queryParams.append('min_employees', params.min_employees.toString());
    if (params?.max_employees !== undefined) queryParams.append('max_employees', params.max_employees.toString());
    if (params?.state) queryParams.append('state', params.state);
    if (params?.is_self_insured !== undefined) queryParams.append('is_self_insured', params.is_self_insured.toString());
    if (params?.plan_type) queryParams.append('plan_type', params.plan_type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.stage) queryParams.append('stage', params.stage);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/lead-builder/company-matches${query}`);
  },

  // Company counts per enrichment stage (funnel chips)
  getCompanyMatchStageCounts: async (params?: {
    icp_profile_id?: string;
    status?: string;
    tier?: number;
    min_employees?: number;
    max_employees?: number;
    state?: string;
    is_self_insured?: boolean;
    plan_type?: string;
    search?: string;
  }): Promise<ApiResponse<{
    total: number;
    matched: number;
    enriched: number;
    contacts_discovered: number;
    outreach_ready: number;
  }>> => {
    const api = getPlatformApi();
    const q = new URLSearchParams();
    if (params?.icp_profile_id) q.append('icp_profile_id', params.icp_profile_id);
    if (params?.status) q.append('status', params.status);
    if (params?.tier !== undefined) q.append('tier', params.tier.toString());
    if (params?.min_employees !== undefined) q.append('min_employees', params.min_employees.toString());
    if (params?.max_employees !== undefined) q.append('max_employees', params.max_employees.toString());
    if (params?.state) q.append('state', params.state);
    if (params?.is_self_insured !== undefined) q.append('is_self_insured', params.is_self_insured.toString());
    if (params?.plan_type) q.append('plan_type', params.plan_type);
    if (params?.search) q.append('search', params.search);
    const query = q.toString() ? `?${q.toString()}` : '';
    return api.get(`/lead-builder/company-matches/stage-counts${query}`);
  },

  // IDs-only for the current filter (powers "select all N matching filter")
  getCompanyMatchIds: async (params?: {
    icp_profile_id?: string;
    status?: string;
    tier?: number;
    min_score?: number;
    min_employees?: number;
    max_employees?: number;
    state?: string;
    is_self_insured?: boolean;
    plan_type?: string;
    search?: string;
    stage?: string;
    max_ids?: number;
  }): Promise<ApiResponse<{ ids: string[]; count: number; capped: boolean }>> => {
    const api = getPlatformApi();
    const q = new URLSearchParams();
    if (params?.icp_profile_id) q.append('icp_profile_id', params.icp_profile_id);
    if (params?.status) q.append('status', params.status);
    if (params?.tier !== undefined) q.append('tier', params.tier.toString());
    if (params?.min_score !== undefined) q.append('min_score', params.min_score.toString());
    if (params?.min_employees !== undefined) q.append('min_employees', params.min_employees.toString());
    if (params?.max_employees !== undefined) q.append('max_employees', params.max_employees.toString());
    if (params?.state) q.append('state', params.state);
    if (params?.is_self_insured !== undefined) q.append('is_self_insured', params.is_self_insured.toString());
    if (params?.plan_type) q.append('plan_type', params.plan_type);
    if (params?.search) q.append('search', params.search);
    if (params?.stage) q.append('stage', params.stage);
    if (params?.max_ids !== undefined) q.append('max_ids', params.max_ids.toString());
    const query = q.toString() ? `?${q.toString()}` : '';
    return api.get(`/lead-builder/company-matches/ids${query}`);
  },

  // Export company matches as CSV
  exportCompanyMatchesCsv: async (params?: {
    icp_profile_id?: string;
    status?: string;
    tier?: number;
    min_employees?: number;
    max_employees?: number;
    state?: string;
    is_self_insured?: boolean;
    plan_type?: string;
    search?: string;
    source_type?: string;
  }): Promise<void> => {
    const queryParams = new URLSearchParams();
    if (params?.icp_profile_id) queryParams.append('icp_profile_id', params.icp_profile_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tier !== undefined) queryParams.append('tier', params.tier.toString());
    if (params?.min_employees !== undefined) queryParams.append('min_employees', params.min_employees.toString());
    if (params?.max_employees !== undefined) queryParams.append('max_employees', params.max_employees.toString());
    if (params?.state) queryParams.append('state', params.state);
    if (params?.is_self_insured !== undefined) queryParams.append('is_self_insured', params.is_self_insured.toString());
    if (params?.plan_type) queryParams.append('plan_type', params.plan_type);
    if (params?.source_type) queryParams.append('source_type', params.source_type);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '';
    const resp = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/lead-builder/company-matches/export-csv${query}`, {
      headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId },
    });
    if (!resp.ok) throw new Error('CSV export failed');
    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'company_matches.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Get single company match
  getCompanyMatch: async (matchId: string): Promise<ApiResponse<CompanyMatch>> => {
    const api = getPlatformApi();
    return api.get<CompanyMatch>(`/lead-builder/company-matches/${matchId}`);
  },

  // Create company match manually
  createCompanyMatch: async (data: Partial<CompanyMatch>): Promise<ApiResponse<CompanyMatch>> => {
    const api = getPlatformApi();
    return api.post<CompanyMatch>('/lead-builder/company-matches', data);
  },

  // Rescore company match
  rescoreCompanyMatch: async (matchId: string): Promise<ApiResponse<CompanyMatch>> => {
    const api = getPlatformApi();
    return api.post<CompanyMatch>(`/lead-builder/company-matches/${matchId}/rescore`, {});
  },

  // Get company match contacts
  getCompanyMatchContacts: async (matchId: string): Promise<ApiResponse<DiscoveredContact[]>> => {
    const api = getPlatformApi();
    return api.get<DiscoveredContact[]>(`/lead-builder/company-matches/${matchId}/contacts`);
  },

  // Get company match intent signals
  getCompanyMatchSignals: async (matchId: string): Promise<ApiResponse<IntentSignal[]>> => {
    const api = getPlatformApi();
    return api.get<IntentSignal[]>(`/lead-builder/company-matches/${matchId}/intent-signals`);
  },

  // ============================================================================
  // JOBS
  // ============================================================================

  // List jobs
  getJobs: async (params?: {
    icp_profile_id?: string;
    status?: string;
    job_type?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<LeadBuilderJob[]>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.icp_profile_id) queryParams.append('icp_profile_id', params.icp_profile_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.job_type) queryParams.append('job_type', params.job_type);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/lead-builder/jobs${query}`);
  },

  // Get single job
  getJob: async (jobId: string): Promise<ApiResponse<LeadBuilderJob>> => {
    const api = getPlatformApi();
    return api.get<LeadBuilderJob>(`/lead-builder/jobs/${jobId}`);
  },

  // Create job
  createJob: async (data: Partial<LeadBuilderJob>): Promise<ApiResponse<LeadBuilderJob>> => {
    const api = getPlatformApi();
    return api.post<LeadBuilderJob>('/lead-builder/jobs', data);
  },

  // Start job
  startJob: async (jobId: string): Promise<ApiResponse<LeadBuilderJob>> => {
    const api = getPlatformApi();
    return api.post<LeadBuilderJob>(`/lead-builder/jobs/${jobId}/start`, {});
  },

  // Pause job
  pauseJob: async (jobId: string): Promise<ApiResponse<LeadBuilderJob>> => {
    const api = getPlatformApi();
    return api.post<LeadBuilderJob>(`/lead-builder/jobs/${jobId}/pause`, {});
  },

  // Cancel job
  cancelJob: async (jobId: string): Promise<ApiResponse<LeadBuilderJob>> => {
    const api = getPlatformApi();
    return api.post<LeadBuilderJob>(`/lead-builder/jobs/${jobId}/cancel`, {});
  },

  // Get job contacts
  getJobContacts: async (jobId: string): Promise<ApiResponse<DiscoveredContact[]>> => {
    const api = getPlatformApi();
    return api.get<DiscoveredContact[]>(`/lead-builder/jobs/${jobId}/contacts`);
  },

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  // Get analytics summary
  getAnalyticsSummary: async (): Promise<ApiResponse<LeadBuilderAnalytics>> => {
    const api = getPlatformApi();
    return api.get<LeadBuilderAnalytics>('/lead-builder/analytics/summary');
  },

  // Get intent breakdown
  getIntentBreakdown: async (): Promise<ApiResponse<Record<string, number>>> => {
    const api = getPlatformApi();
    return api.get<Record<string, number>>('/lead-builder/analytics/intent-breakdown');
  },

  // ============================================================================
  // DISCOVERY ENGINE
  // ============================================================================

  // Start discovery job using the discovery engine
  startDiscovery: async (data: {
    icp_profile_id: string;
    job_name?: string;
    max_companies?: number;
    max_tier?: number;
    enabled_connectors?: string[];
    config?: Record<string, unknown>;
  }): Promise<ApiResponse<{ status: string; job_id: string; job_name: string; message: string }>> => {
    const api = getPlatformApi();
    return api.post<{ status: string; job_id: string; job_name: string; message: string }>('/lead-builder/discovery/start', data);
  },

  // Import providers from directory using a provider-type ICP
  runProviderImport: async (data: {
    icp_profile_id: string;
    max_results?: number;
    include_directory_db?: boolean;
    discover_contacts?: boolean;
    contact_max_tier?: number;
  }): Promise<ApiResponse<{
    status: string;
    job_id: string;
    job_name: string;
    job_type: string;
    providers_scored: number;
    providers_imported: number;
    providers_skipped_duplicate: number;
    message: string;
  }>> => {
    const api = getPlatformApi();
    return api.post('/lead-builder/provider-import', data);
  },

  // Enrich existing imported providers with contact discovery waterfall
  enrichImportedProviders: async (data: {
    icp_profile_id: string;
    max_providers?: number;
    max_tier?: number;
    only_missing_contacts?: boolean;
  }): Promise<ApiResponse<{
    status: string;
    providers_queued: number;
    providers_processed: number;
    contacts_discovered: number;
    irs_sourced_count: number;
    api_keys_configured: Record<string, boolean>;
    enriched_providers: Array<{ company_name: string; contacts_found: number; sources: string[] }>;
    errors: string[];
    message: string;
  }>> => {
    const api = getPlatformApi();
    return api.post('/lead-builder/provider-enrich', data);
  },

  // Assign ICP profile to existing company matches (e.g. orphaned DOL imports)
  assignIcpToMatches: async (data: {
    icp_profile_id: string;
    match_ids?: string[];
    filters?: { source_type?: string; states?: string[]; min_confidence?: number };
  }): Promise<ApiResponse<{
    status: string;
    icp_profile_id: string;
    icp_profile_name: string;
    matches_assigned: number;
  }>> => {
    const api = getPlatformApi();
    return api.post('/lead-builder/company-matches/assign-icp', data);
  },

  // List discovery sources
  getDiscoverySources: async (tier?: number): Promise<ApiResponse<Array<{
    source_name: string;
    source_type: string;
    source_tier: number;
    description: string | null;
    requires_api_key: boolean;
    requires_oauth: boolean;
    pricing_model: string | null;
    is_system_enabled: boolean;
    is_org_configured: boolean;
  }>>> => {
    const api = getPlatformApi();
    const params = tier ? `?tier=${tier}` : '';
    return api.get(`/lead-builder/discovery/sources${params}`);
  },

  // Get discovery runs
  getDiscoveryRuns: async (jobId?: string): Promise<ApiResponse<Array<{
    id: string;
    job_id: string;
    source_name: string;
    records_fetched: number;
    records_matched: number;
    status: string;
    started_at: string;
    completed_at: string | null;
    duration_seconds: number | null;
  }>>> => {
    const api = getPlatformApi();
    const params = jobId ? `?job_id=${jobId}` : '';
    return api.get(`/lead-builder/discovery/runs${params}`);
  },

  // Enrich company
  enrichCompany: async (companyId: string, tier?: number): Promise<ApiResponse<{
    status: string;
    company_id: string;
    missing_fields?: string[];
    message: string;
  }>> => {
    const api = getPlatformApi();
    const params = tier ? `?tier=${tier}` : '';
    return api.post(`/lead-builder/company-matches/${companyId}/enrich${params}`, {});
  },

  // Backfill missing domains (provider enrichment -> provider-api -> Brave)
  backfillDomains: async (params?: {
    limit?: number;
    icp_profile_id?: string;
    use_brave?: boolean;
  }): Promise<ApiResponse<{
    status: string;
    considered: number;
    resolved: number;
    counts: {
      from_enrichment: number;
      from_provider_api: number;
      from_brave: number;
      unresolved: number;
    };
    brave_enabled: boolean;
  }>> => {
    const api = getPlatformApi();
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.icp_profile_id) q.set('icp_profile_id', params.icp_profile_id);
    if (params?.use_brave != null) q.set('use_brave', String(params.use_brave));
    const query = q.toString() ? `?${q.toString()}` : '';
    return api.post(`/lead-builder/company-matches/backfill-domains${query}`, {});
  },

  // ============================================================================
  // PROVIDER CONFIGURATION
  // ============================================================================

  // List provider configs
  getProviderConfigs: async (): Promise<ApiResponse<Array<{
    id: string;
    provider_name: string;
    provider_tier: number;
    is_enabled: boolean;
    is_verified: boolean;
    monthly_budget_usd: number | null;
    monthly_usage_usd: number;
    total_requests: number;
    created_at: string;
    updated_at: string;
  }>>> => {
    const api = getPlatformApi();
    return api.get('/lead-builder/providers');
  },

  // Configure provider
  configureProvider: async (providerName: string, data: {
    api_key?: string;
    api_secret?: string;
    monthly_budget_usd?: number;
    config?: Record<string, unknown>;
  }): Promise<ApiResponse<{ id: string; provider_name: string; is_verified: boolean }>> => {
    const api = getPlatformApi();
    return api.post(`/lead-builder/providers/${providerName}`, data);
  },

  // Verify provider
  verifyProvider: async (providerName: string): Promise<ApiResponse<{ status: string; message: string; is_verified?: boolean; error?: string }>> => {
    const api = getPlatformApi();
    return api.post(`/lead-builder/providers/${providerName}/verify`, {});
  },

  deleteProvider: async (providerName: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.delete(`/lead-builder/providers/${providerName}`);
  },

  // Update provider config
  updateProviderConfig: async (providerName: string, data: {
    is_enabled?: boolean;
    monthly_budget_usd?: number;
  }): Promise<ApiResponse<{ id: string; is_enabled: boolean; monthly_budget_usd: number }>> => {
    const api = getPlatformApi();
    return api.patch(`/lead-builder/providers/${providerName}`, data);
  },

  // ============================================================================
  // CONTACTS
  // ============================================================================

  // Create discovered contact
  createDiscoveredContact: async (data: Partial<DiscoveredContact>): Promise<ApiResponse<DiscoveredContact>> => {
    const api = getPlatformApi();
    return api.post<DiscoveredContact>('/lead-builder/discovered-contacts', data);
  },

  // Add contact to list
  addContactToList: async (contactId: string, listId: string): Promise<ApiResponse<DiscoveredContact>> => {
    const api = getPlatformApi();
    return api.post<DiscoveredContact>(`/lead-builder/discovered-contacts/${contactId}/add-to-list`, { contact_list_id: listId });
  },

  // Create intent signal
  createIntentSignal: async (data: Partial<IntentSignal>): Promise<ApiResponse<IntentSignal>> => {
    const api = getPlatformApi();
    return api.post<IntentSignal>('/lead-builder/intent-signals', data);
  },

  // Discover contacts for a company
  discoverContacts: async (matchId: string, maxTier?: number): Promise<ApiResponse<{
    status: string;
    company_id: string;
    total_contacts: number;
    decision_makers: number;
    contacts_saved: number;
    sources_used: string[];
    domain_discovered?: string;
    errors: string[];
  }>> => {
    const api = getPlatformApi();
    const params = maxTier ? `?max_tier=${maxTier}` : '';
    return api.post(`/lead-builder/company-matches/${matchId}/discover-contacts${params}`, {});
  },

  // Bulk discover contacts
  bulkDiscoverContacts: async (params?: {
    match_ids?: string[];
    tier_filter?: number;
    max_companies?: number;
    max_tier?: number;
  }): Promise<ApiResponse<{
    status: string;
    companies_processed: number;
    companies_total: number;
    total_contacts: number;
    total_decision_makers: number;
    results?: Array<{
      company_id: string;
      company_name: string;
      contacts_found: number;
      decision_makers?: number;
      contacts_saved?: number;
      sources_used?: string[];
      error?: string;
    }>;
    message?: string;
  }>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.tier_filter !== undefined) queryParams.append('tier_filter', params.tier_filter.toString());
    if (params?.max_companies !== undefined) queryParams.append('max_companies', params.max_companies.toString());
    if (params?.max_tier !== undefined) queryParams.append('max_tier', params.max_tier.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.post(`/lead-builder/company-matches/bulk-discover-contacts${query}`, {
      match_ids: params?.match_ids,
    });
  },

  // Add contacts to cold outreach campaign
  // Named decision-makers across all companies, with their source (TRUS-770)
  getDecisionMakers: async (params: {
    source?: string;
    role_class?: string;
    email_status?: string;
    review_status?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<DecisionMakersPage>> => {
    const api = getPlatformApi();
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.append(k, String(v));
    });
    return api.get(`/lead-builder/decision-makers?${qp.toString()}`);
  },

  // Run the registry-first waterfall over every imported provider org not yet searched
  discoverDecisionMakers: async (
    scope: 'registries' | 'registries_and_websites' | 'all',
  ): Promise<ApiResponse<{ status: string; job_id: string | null; companies_total: number; message: string }>> => {
    const api = getPlatformApi();
    return api.post(`/lead-builder/decision-makers/discover`, { scope });
  },

  // Contacts a person should check before outreach (TRUS-770)
  getContactReviewQueue: async (limit = 100): Promise<ApiResponse<{ items: ContactReviewItem[]; count: number }>> => {
    const api = getPlatformApi();
    return api.get(`/lead-builder/contacts/review-queue?limit=${limit}`);
  },

  // Approve or reject a discovered contact
  reviewContact: async (
    contactId: string,
    decision: 'approved' | 'rejected',
    note?: string,
  ): Promise<ApiResponse<{ id: string; review_status: string }>> => {
    const api = getPlatformApi();
    return api.post(`/lead-builder/contacts/${contactId}/review`, { decision, note });
  },

  addContactsToCampaign: async (campaignId: string, contactIds: string[]): Promise<ApiResponse<{
    added: number;
    skipped: number;
    errors: string[];
  }>> => {
    const api = getPlatformApi();
    return api.post(`/cold-outreach/campaigns/${campaignId}/prospects/bulk`, {
      prospects: contactIds.map(id => ({ discovered_contact_id: id })),
    });
  },

  // Get waterfall configuration (all tiers, tools, config status)
  getWaterfallConfig: async (): Promise<ApiResponse<{
    tiers: Array<{
      tier_number: number;
      tier_name: string;
      tier_description: string;
      tools: Array<{
        source_name: string;
        display_name: string;
        source_type: string;
        is_system_enabled: boolean;
        is_org_configured: boolean;
        is_verified: boolean;
        requires_api_key: boolean;
        pricing_model: string | null;
        monthly_budget_usd: number | null;
        monthly_usage_usd: number;
        total_requests: number;
      }>;
      is_fully_configured: boolean;
      contacts_available_from_tier: boolean;
    }>;
    total_configured_tools: number;
    total_available_tools: number;
    total_monthly_budget_usd: number;
    total_monthly_usage_usd: number;
  }>> => {
    const api = getPlatformApi();
    return api.get('/lead-builder/waterfall/config');
  },

  // Get per-company waterfall discovery status
  getCompanyWaterfallStatus: async (matchId: string): Promise<ApiResponse<{
    company_match_id: string;
    company_name: string;
    domain: string | null;
    total_contacts: number;
    total_decision_makers: number;
    last_discovery_at: string | null;
    tier_results: Array<{
      tier_number: number;
      tier_name: string;
      status: 'completed' | 'skipped' | 'not_run' | 'error';
      contacts_found: number;
      decision_makers_found: number;
      sources_used: string[];
      error_message: string | null;
    }>;
    contacts_by_tier: Record<number, any[]>;
  }>> => {
    const api = getPlatformApi();
    return api.get(`/lead-builder/company-matches/${matchId}/waterfall-status`);
  },

  // Create a new outreach list (campaign) from discovered contacts
  createOutreachList: async (data: {
    campaign_name: string;
    campaign_description?: string;
    campaign_type?: string;
    contact_ids?: string[];
    company_match_ids?: string[];
    buyer_persona?: string[];
    min_confidence?: number;
    data_source_tier?: string[];
    decision_makers_only?: boolean;
    require_email?: boolean;
    create_tracked_list?: boolean;
  }): Promise<ApiResponse<{
    campaign_id: string;
    list_id: string | null;
    campaign_name: string;
    prospects_added: number;
    prospects_skipped: number;
    errors: Array<{ contact_id: string; error: string }>;
  }>> => {
    const api = getPlatformApi();
    return api.post('/lead-builder/contacts/create-outreach-list', data);
  },

  // ---- Trackable Outreach Lists ----
  getOutreachLists: async (status?: string): Promise<ApiResponse<any[]>> => {
    const api = getPlatformApi();
    return api.get(`/lead-builder/outreach-lists${status ? `?status=${status}` : ''}`);
  },
  getOutreachList: async (id: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.get(`/lead-builder/outreach-lists/${id}`);
  },
  createTrackedOutreachList: async (data: {
    name: string;
    description?: string;
    company_match_ids: string[];
    icp_profile_id?: string;
  }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.post('/lead-builder/outreach-lists', data);
  },
  updateOutreachList: async (id: string, data: { name?: string; description?: string; status?: string }): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.patch(`/lead-builder/outreach-lists/${id}`, data);
  },
  deleteOutreachList: async (id: string): Promise<ApiResponse<any>> => {
    const api = getPlatformApi();
    return api.delete(`/lead-builder/outreach-lists/${id}`);
  },

  // List available campaigns for selector dropdown
  getAvailableCampaigns: async (status?: string): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    status: string;
    campaign_type: string;
    total_prospects: number;
    created_at: string;
  }>>> => {
    const api = getPlatformApi();
    const params = status ? `?campaign_status=${status}` : '';
    return api.get(`/lead-builder/campaigns/available${params}`);
  },
};

// ============================================================================
// MARKETPLACE INTEGRATIONS TYPES
// ============================================================================

export interface MarketplaceInstallation {
  id: string;
  integration_id: string;
  integration_name: string;
  category: string;
  status: 'pending' | 'active' | 'paused' | 'failed' | 'disconnected';
  installed_at: string;
  activated_at?: string;
  auth_type: string;
  sync_enabled: boolean;
  sync_frequency: string;
  last_sync_at?: string;
  next_sync_at?: string;
  total_syncs: number;
  successful_syncs: number;
  failed_syncs: number;
  records_synced: number;
  last_error?: string;
  last_error_at?: string;
  consecutive_failures: number;
  created_at: string;
  updated_at: string;
}

export interface MarketplaceFieldMapping {
  id: string;
  installation_id: string;
  source_field: string;
  target_field: string;
  field_type: string;
  transformation?: string;
  transformation_config: Record<string, unknown>;
  default_value?: string;
  is_required: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarketplaceSyncHistory {
  id: string;
  installation_id: string;
  sync_type: 'full' | 'incremental' | 'webhook';
  status: 'running' | 'completed' | 'failed' | 'partial';
  direction: 'inbound' | 'outbound';
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_deleted: number;
  records_failed: number;
  records_skipped: number;
  sync_details: Record<string, unknown>;
  error_details: Array<Record<string, unknown>>;
  warnings: Array<Record<string, unknown>>;
  created_at: string;
}

export interface AvailableIntegration {
  id: string;
  name: string;
  description: string;
  category: 'hris' | 'peo' | 'payroll' | 'benefits' | 'communication' | 'crm';
  logo_url?: string;
  auth_type: 'oauth2' | 'api_key' | 'basic';
  features: string[];
  is_popular: boolean;
}

// ============================================================================
// MARKETPLACE INTEGRATIONS API
// ============================================================================

export const marketplaceApi = {
  // ============================================================================
  // AVAILABLE INTEGRATIONS
  // ============================================================================

  // List available integrations
  getAvailableIntegrations: async (params?: { category?: string }): Promise<ApiResponse<AvailableIntegration[]>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/marketplace/available${query}`);
  },

  // ============================================================================
  // INSTALLATIONS
  // ============================================================================

  // List installations
  getInstallations: async (params?: { status?: string }): Promise<ApiResponse<MarketplaceInstallation[]>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/marketplace/installations${query}`);
  },

  // Get single installation
  getInstallation: async (installationId: string): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.get<MarketplaceInstallation>(`/marketplace/installations/${installationId}`);
  },

  // Create installation
  createInstallation: async (data: { integration_id: string; config?: Record<string, unknown> }): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.post<MarketplaceInstallation>('/marketplace/installations', data);
  },

  // Update installation
  updateInstallation: async (installationId: string, data: Partial<MarketplaceInstallation>): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.put<MarketplaceInstallation>(`/marketplace/installations/${installationId}`, data);
  },

  // Delete installation
  deleteInstallation: async (installationId: string): Promise<ApiResponse<void>> => {
    const api = getPlatformApi();
    return api.delete(`/marketplace/installations/${installationId}`);
  },

  // Pause installation
  pauseInstallation: async (installationId: string): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.post<MarketplaceInstallation>(`/marketplace/installations/${installationId}/pause`, {});
  },

  // Resume installation
  resumeInstallation: async (installationId: string): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.post<MarketplaceInstallation>(`/marketplace/installations/${installationId}/resume`, {});
  },

  // ============================================================================
  // OAUTH
  // ============================================================================

  // Get OAuth URL
  getOAuthUrl: async (integrationId: string): Promise<ApiResponse<{ auth_url: string }>> => {
    const api = getPlatformApi();
    return api.get<{ auth_url: string }>(`/marketplace/oauth/${integrationId}/authorize`);
  },

  // Complete OAuth callback
  completeOAuth: async (integrationId: string, code: string, state?: string): Promise<ApiResponse<MarketplaceInstallation>> => {
    const api = getPlatformApi();
    return api.post<MarketplaceInstallation>(`/marketplace/oauth/${integrationId}/callback`, { code, state });
  },

  // ============================================================================
  // FIELD MAPPINGS
  // ============================================================================

  // Get field mappings
  getFieldMappings: async (installationId: string): Promise<ApiResponse<MarketplaceFieldMapping[]>> => {
    const api = getPlatformApi();
    return api.get<MarketplaceFieldMapping[]>(`/marketplace/installations/${installationId}/field-mappings`);
  },

  // Update field mappings
  updateFieldMappings: async (installationId: string, mappings: Partial<MarketplaceFieldMapping>[]): Promise<ApiResponse<MarketplaceFieldMapping[]>> => {
    const api = getPlatformApi();
    return api.put<MarketplaceFieldMapping[]>(`/marketplace/installations/${installationId}/field-mappings`, { mappings });
  },

  // ============================================================================
  // SYNC
  // ============================================================================

  // Trigger sync
  triggerSync: async (installationId: string, syncType: 'full' | 'incremental' = 'incremental'): Promise<ApiResponse<MarketplaceSyncHistory>> => {
    const api = getPlatformApi();
    return api.post<MarketplaceSyncHistory>(`/marketplace/installations/${installationId}/sync`, { sync_type: syncType });
  },

  // Get sync history
  getSyncHistory: async (installationId: string, params?: { status?: string; page?: number; page_size?: number }): Promise<ApiResponse<MarketplaceSyncHistory[]>> => {
    const api = getPlatformApi();
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/marketplace/installations/${installationId}/sync-history${query}`);
  },

  // Get single sync
  getSync: async (installationId: string, syncId: string): Promise<ApiResponse<MarketplaceSyncHistory>> => {
    const api = getPlatformApi();
    return api.get<MarketplaceSyncHistory>(`/marketplace/installations/${installationId}/sync-history/${syncId}`);
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export default getPlatformApi;
